/**
 * SearchFulfillmentPanel.jsx
 * 
 * Search-specific fulfillment panel for non-Tax products
 * (Current Owner Search, Title Search, O&E, Full Search, etc.)
 * 
 * Sections:
 *  1. Search Setup (Parcel IDs, Effective Date/Time)
 *  2. Deeds / Chain of Title (table with add/remove)
 *  3. Title Vesting (auto-generated, editable)
 *  4. Legal Description (textarea)
 *  5. DOT / Mortgage Information (repeatable structured records)
 *  6. Documents (primary doc selector)
 *  7. Finalize / Complete (validation + complete button)
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FulfillmentSummary } from '../orders/FulfillmentSummary';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import TypingOrderSection from '../typing/TypingOrderSection';
import ScrollToTopButton from '../ui/ScrollToTopButton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { Calendar as CalendarComponent } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { autoPartyCase, autoCommentsCase } from '../../utils/properCase';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { MapPin, FileText, Scale, Landmark, ScrollText, ShieldCheck, AlertTriangle, CheckCircle2, Plus, FileCheck, Clock, Calendar, ChevronDown, ChevronUp, Maximize2, RotateCcw, Pencil, Upload, Loader2, Search, Check, X, Receipt, AlertCircle, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import ResearchLinksCard from './ResearchLinksCard';
import { PartyTextarea } from './PartyTextarea';
import { format, parse, isValid } from 'date-fns';
import { applyTemplate } from '../../constants/reasonTemplates';
import { getRoleDashboardPath } from '../../utils/roleDashboard';
import { getBusinessToday, isFutureDate, futureDateClass, FutureDateError } from '../../utils/dateGuards';
import FinalReviewModal from './FinalReviewModal';
import { ProductWithCode } from '../orders/ProductWithCode';
import { useAuth } from '../../App';

import './search-fulfillment-premium.css';

const API = process.env.REACT_APP_BACKEND_URL;

// MATURITY-EXEMPT-001 — maturity dates may legitimately be in the future
const FUTURE_EXEMPT_KEYS = new Set(['maturity_date', 'new_maturity_date']);
const noFuture = (field) => field.kind === 'date' && !FUTURE_EXEMPT_KEYS.has(field.key);

// CURRENCY-FMT-001 — US currency formatting ($1,250,000.00) applied on blur
const CURRENCY_KEYS = new Set(['original_amount', 'new_principal_amount', 'judgment_amount', 'consideration_amount', 'land_value', 'improvement_value', 'total_assessed_value']);
const isCurrency = (field) => CURRENCY_KEYS.has(field.key);
// CASE-AUTO-002 — automatic readable casing on BLUR only, and only when the
// value is predominantly ALL CAPS (mixed case is kept exactly as entered).
// Rule P (party/name + short descriptive) / Rule C (comments & notes).
// Category-4 fields (identifiers, legal description, dates, selects) get
// no formatter. Backend save stays trim-only (historical records untouched).
const SHORT_DESCRIPTIVE_KEYS = new Set(['judgment_type', 'document_type']);
const blurFormatter = (field) => {
  if (isCurrency(field)) return formatUSCurrency;
  if (field.kind === 'select' || field.kind === 'date' || field.readOnly) return null;
  if (field.properCase || SHORT_DESCRIPTIVE_KEYS.has(field.key)) return autoPartyCase;
  if (field.kind === 'textarea' && field.key === 'comments') return autoCommentsCase;
  return null;
};
const formatUSCurrency = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  const num = parseFloat(s.replace(/[^0-9.\-]/g, ''));
  if (!isFinite(num)) return s;
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

// DATE-MASK-001 — auto-insert slashes while typing (08142026 → 08/14/2026)
const maskDateInput = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length < 3) return digits;
  if (digits.length < 5) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

// ─── Utility: generate unique IDs ───
let _counter = 0;
const uid = () => `sf_${Date.now()}_${++_counter}`;

// ─── DOT / Mortgage Information field groups (maps to search report layout) ───
const DOT_FLAT_GROUPS = [
  {
    title: 'Instrument',
    fields: [
      { key: 'type', label: 'Type', kind: 'select', options: ['Mortgage', 'Deed of Trust'] },
      { key: 'open_end', label: 'Open End', kind: 'select', options: ['Yes', 'No'] },
      { key: 'original_amount', label: 'Original Amount', placeholder: 'e.g. $250,000.00' },
      { key: 'dated_date', label: 'Dated Date', kind: 'date' },
      { key: 'date_recorded', label: 'Date Recorded', kind: 'date' },
      { key: 'maturity_date', label: 'Maturity Date', kind: 'date' },
      { key: 'instrument_number', label: 'Instrument Number' },
      { key: 'book', label: 'Book' },
      { key: 'page', label: 'Page' },
    ],
  },
  {
    title: 'Parties',
    fields: [
      { key: 'trustee', label: 'Trustee', properCase: true },
      { key: 'lender', label: 'Lender', properCase: true, multiline: true },
      { key: 'borrower', label: 'Borrower', properCase: true, multiline: true },
    ],
  },
];

// ─── Judgment / Lien Types (JUDGMENT-LIEN-001, Aug 2026) ───
// Top-level, order-scoped repeatable section — formerly the per-mortgage
// "Legal Action" flat group. Legacy per-mortgage data is lifted on load.
const LEGAL_ACTION_FIELDS = [
  { key: 'plaintiff', label: 'Plaintiff', properCase: true, multiline: true },
  { key: 'defendant', label: 'Defendant', properCase: true, multiline: true },
  { key: 'case_number', label: 'Case Number' },
  { key: 'dated_date', label: 'Dated Date', kind: 'date' },
  { key: 'recorded_date', label: 'Recorded Date', kind: 'date' },
  { key: 'book', label: 'Book' },
  { key: 'page', label: 'Page' },
  { key: 'instrument_number', label: 'Instrument Number' },
  { key: 'judgment_type', label: 'Judgment Type' },
  { key: 'judgment_amount', label: 'Judgment Amount', placeholder: 'e.g. $15,000' },
  { key: 'comments', label: 'Comments', kind: 'textarea', placeholder: 'Free-form notes for this judgment / lien...' },
];

const emptyLegalAction = () => {
  const rec = { id: uid() };
  LEGAL_ACTION_FIELDS.forEach(f => { rec[f.key] = ''; });
  return rec;
};

const legalActionHasData = (rec) =>
  LEGAL_ACTION_FIELDS.some(f => (rec[f.key] || '').trim());

// Legacy per-mortgage legal-action keys → new top-level entry keys
const LEGACY_LA_KEY_MAP = {
  plaintiff: 'plaintiff',
  defendant: 'defendant',
  case_number: 'case_number',
  la_dated_date: 'dated_date',
  la_recorded_date: 'recorded_date',
  la_book: 'book',
  la_page: 'page',
  la_instrument_number: 'instrument_number',
  judgment_type: 'judgment_type',
  judgment_amount: 'judgment_amount',
  la_comments: 'comments',
};

const liftLegacyLegalAction = (m) => {
  if (!Object.keys(LEGACY_LA_KEY_MAP).some(k => (m[k] || '').trim())) return null;
  const rec = emptyLegalAction();
  Object.entries(LEGACY_LA_KEY_MAP).forEach(([oldK, newK]) => { rec[newK] = m[oldK] || ''; });
  return rec;
};

// Repeatable related records under each Mortgage/DOT (own +/− controls)
const DOT_SUB_SECTIONS = [
  {
    key: 'assignments',
    title: 'Assignment',
    fields: [
      { key: 'assignor', label: 'Assignor', properCase: true, multiline: true },
      { key: 'assignee', label: 'Assignee', properCase: true, multiline: true },
      // Related Party / Sub Lender / Senior Lender removed per client request (Aug 2026)
      // Recording information — per-assignment (Aug 2026, mirrors Mortgage layout)
      { key: 'dated_date', label: 'Dated Date', kind: 'date', group: 'Recording Information' },
      { key: 'recorded_date', label: 'Recorded Date', kind: 'date', group: 'Recording Information' },
      { key: 'book', label: 'Book', group: 'Recording Information' },
      { key: 'page', label: 'Page', group: 'Recording Information' },
      { key: 'instrument_number', label: 'Instrument Number', group: 'Recording Information' },
      { key: 'comments', label: 'Comments / Notes', kind: 'textarea', group: 'Additional Information', placeholder: 'Comments / notes for this assignment…' },
    ],
  },
  {
    key: 'modifications',
    title: 'Modification',
    fields: [
      { key: 'modified_to', label: 'Modified To', properCase: true, multiline: true },
      { key: 'new_principal_amount', label: 'New Principal Amount', placeholder: 'e.g. $200,000.00' },
      { key: 'borrower_name', label: 'Borrower Name', properCase: true, multiline: true },
      { key: 'lender', label: 'Lender', properCase: true, multiline: true },
      { key: 'dated_date', label: 'Dated Date', kind: 'date' },
      { key: 'recorded_date', label: 'Recorded Date', kind: 'date' },
      { key: 'new_maturity_date', label: 'New Maturity Date', kind: 'date' },
      { key: 'book', label: 'Book' },
      { key: 'page', label: 'Page' },
      { key: 'instrument_number', label: 'Instrument Number' },
      { key: 'comments', label: 'Comments', kind: 'textarea', placeholder: 'Comments / notes for this modification…' },
    ],
  },
  {
    key: 'appointed_trustees',
    title: 'Appointed Trustee',
    fields: [
      { key: 'appointed_trustee', label: 'Appointed Trustee', properCase: true, multiline: true },
      { key: 'prior_trustee', label: 'Prior Trustee', properCase: true, multiline: true },
      { key: 'dated_date', label: 'Dated Date', kind: 'date' },
      { key: 'recorded_date', label: 'Recorded Date', kind: 'date' },
      { key: 'book', label: 'Book' },
      { key: 'page', label: 'Page' },
      { key: 'instrument_number', label: 'Instrument Number' },
      // AT-FIELDS-002 (Sep 2026)
      { key: 'executed_by', label: 'Executed By', properCase: true },
      { key: 'comments', label: 'Comments', kind: 'textarea', placeholder: 'Additional information for this appointed trustee…' },
    ],
  },
  {
    key: 'subordination_agreements',
    title: 'Subordination Agreement',
    fields: [
      { key: 'borrower', label: 'Borrower', properCase: true, multiline: true },
      { key: 'senior_lender', label: 'Senior Lender', properCase: true, multiline: true },
      { key: 'subordinated_lender', label: 'Subordinated Lender', properCase: true, multiline: true },
      { key: 'dated_date', label: 'Dated Date', kind: 'date' },
      { key: 'recorded_date', label: 'Recorded Date', kind: 'date' },
      { key: 'book', label: 'Book' },
      { key: 'page', label: 'Page' },
      { key: 'instrument_number', label: 'Instrument Number' },
      { key: 'comments', label: 'Comments', kind: 'textarea', placeholder: 'Comments / notes for this subordination agreement…' },
    ],
  },
  {
    key: 'notices_of_pendency',
    title: 'Notice of Pendency',
    fields: [
      { key: 'plaintiff', label: 'Plaintiff', properCase: true, multiline: true },
      { key: 'defendant', label: 'Defendant', properCase: true, multiline: true },
      { key: 'case_number', label: 'Case Number' },
      { key: 'dated_date', label: 'Dated Date', kind: 'date' },
      { key: 'recorded_date', label: 'Recorded Date', kind: 'date' },
      { key: 'book', label: 'Book' },
      { key: 'page', label: 'Page' },
      { key: 'instrument_number', label: 'Instrument Number' },
      { key: 'document_type', label: 'Document Type' },
      { key: 'comments', label: 'Comments', kind: 'textarea', placeholder: 'Comments / notes for this notice of pendency…' },
    ],
  },
];

const DOT_FLAT_KEYS = DOT_FLAT_GROUPS.flatMap(g => g.fields.map(f => f.key));

const emptySubRecord = (section) => {
  const rec = { id: uid() };
  section.fields.forEach(f => { rec[f.key] = ''; });
  return rec;
};

const subRecordHasData = (section, sub) =>
  section.fields.some(f => {
    // ASSIGN-CHAIN-002: an auto-derived (linked) assignor alone is not data
    if (section.key === 'assignments' && f.key === 'assignor' && sub.assignor_linked) return false;
    return (sub[f.key] || '').trim();
  });

// ASSIGN-CHAIN-002 (Aug 2026) — Assignor derives from the chain:
// #1 ← Mortgage/DOT Lender, #n ← Assignment #(n-1) Assignee.
// ASSIGN-OVERRIDE-003 (Jun 2026) — AUTO means pre-populated default, NOT
// read-only: a processor override (assignor_overridden) is sticky and is
// never re-derived by autosave/reload/upstream edits until explicitly reset.
// Pass oldM so clearing an upstream source also clears its dependent assignor.
const syncAssignmentChain = (m, oldM) => {
  let prev = (m.lender || '').trim() ? m.lender : '';
  let oldPrev = oldM ? (oldM.lender || '') : null;
  const oldSubs = oldM ? (oldM.assignments || []) : [];
  const subs = (m.assignments || []).map((s, i) => {
    let next = s;
    if (s.assignor_overridden) {
      next = s.assignor_linked ? { ...s, assignor_linked: false } : s;
    } else if (prev) {
      next = (s.assignor === prev && s.assignor_linked) ? s : { ...s, assignor: prev, assignor_linked: true };
    } else {
      const wasDerived = oldPrev !== null && oldPrev.trim() && (s.assignor || '') === oldPrev;
      const kept = wasDerived ? '' : (s.assignor || '');
      next = (s.assignor === kept && !s.assignor_linked) ? s : { ...s, assignor: kept, assignor_linked: false };
    }
    prev = (next.assignee || '').trim() ? next.assignee : '';
    if (oldPrev !== null) oldPrev = (oldSubs[i] && oldSubs[i].assignee) || '';
    return next;
  });
  return { ...m, assignments: subs };
};

const emptyDotMortgage = () => {
  const rec = { id: uid(), comments: '' };
  DOT_FLAT_KEYS.forEach(k => { rec[k] = ''; });
  DOT_SUB_SECTIONS.forEach(s => { rec[s.key] = [emptySubRecord(s)]; });
  return rec;
};

const dotMortgageHasData = (rec) =>
  DOT_FLAT_KEYS.some(k => (rec[k] || '').trim()) ||
  (rec.comments || '').trim() ||
  DOT_SUB_SECTIONS.some(s => (rec[s.key] || []).some(sub => subRecordHasData(s, sub)));

// Legacy flat keys (pre-June-2026 structure) lifted into sub-record arrays on load
const LEGACY_ASSIGNMENT_KEYS = ['assignor', 'assignee'];
// Assignment fields removed per client request (Aug 2026) — stripped on load/save
const REMOVED_ASSIGNMENT_KEYS = ['related_party', 'sub_lender', 'senior_lender'];
const LEGACY_MODIFICATION_KEYS = ['modified_to', 'new_principal_amount'];
// AT-TABS-001 (Aug 2026) — legacy flat Appointed Trustee fields lifted into
// the repeatable appointed_trustees[] sub-records on load.
const AT_LIFT_MAP = {
  appointed_trustee: 'appointed_trustee',
  prior_trustee: 'prior_trustee',
  at_dated_date: 'dated_date',
  at_recorded_date: 'recorded_date',
  at_book: 'book',
  at_page: 'page',
  at_instrument_number: 'instrument_number',
};

const normalizeDotMortgage = (m) => {
  const rec = { ...emptyDotMortgage(), ...m, id: m.id || uid(), comments: m.comments || '' };
  DOT_SUB_SECTIONS.forEach(s => {
    const arr = Array.isArray(m[s.key]) ? m[s.key] : [];
    rec[s.key] = arr.length
      ? arr.map(sub => ({ ...emptySubRecord(s), ...sub, id: sub.id || uid() }))
      : [emptySubRecord(s)];
  });
  if (!Array.isArray(m.assignments) && LEGACY_ASSIGNMENT_KEYS.some(k => (m[k] || '').trim())) {
    LEGACY_ASSIGNMENT_KEYS.forEach(k => { rec.assignments[0][k] = m[k] || ''; });
  }
  if (!Array.isArray(m.modifications) && LEGACY_MODIFICATION_KEYS.some(k => (m[k] || '').trim())) {
    LEGACY_MODIFICATION_KEYS.forEach(k => { rec.modifications[0][k] = m[k] || ''; });
  }
  if (!(Array.isArray(m.appointed_trustees) && m.appointed_trustees.length) &&
      Object.keys(AT_LIFT_MAP).some(k => (m[k] || '').trim())) {
    Object.entries(AT_LIFT_MAP).forEach(([oldK, newK]) => { rec.appointed_trustees[0][newK] = m[oldK] || ''; });
  }
  [...LEGACY_ASSIGNMENT_KEYS, ...REMOVED_ASSIGNMENT_KEYS, ...LEGACY_MODIFICATION_KEYS, ...Object.keys(LEGACY_LA_KEY_MAP), ...Object.keys(AT_LIFT_MAP), 'mortgagor', 'mortgagee'].forEach(k => { delete rec[k]; });
  // ASSIGN-OVERRIDE-003 — keep stored overrides; re-derive only non-overridden
  // assignors (a non-empty source fills them, an empty source keeps manual text).
  rec.assignments = syncAssignmentChain(rec).assignments;
  (rec.assignments || []).forEach(a => { REMOVED_ASSIGNMENT_KEYS.forEach(k => { delete a[k]; }); });
  return rec;
};

const sanitizeDotRecords = (records) =>
  records.filter(dotMortgageHasData).map(rec => {
    const out = { ...rec };
    DOT_SUB_SECTIONS.forEach(s => {
      if (s.key === 'assignments') {
        // ASSIGN-CHAIN-002: keep an explicitly added derived-only assignment
        // (its linked assignor came from the previous record's assignee) so
        // the chain survives save/reload; a lone auto-filled placeholder
        // (derived from the Lender only) is still dropped.
        const arr = rec.assignments || [];
        out.assignments = arr.filter((sub, i) =>
          subRecordHasData(s, sub) ||
          (i > 0 && sub.assignor_linked && (sub.assignor || '').trim() && subRecordHasData(s, arr[i - 1])));
      } else {
        out[s.key] = (rec[s.key] || []).filter(sub => subRecordHasData(s, sub));
      }
    });
    out.assignments = (out.assignments || []).map(a => {
      const clean = { ...a };
      REMOVED_ASSIGNMENT_KEYS.forEach(k => { delete clean[k]; });
      delete clean.assignor_linked;
      // ASSIGN-OVERRIDE-003 — persist the override flag so reload never re-derives
      if (!clean.assignor_overridden) delete clean.assignor_overridden;
      return clean;
    });
    return out;
  });

// ─── Shared input class for consistent styling ───
const inputClass = "w-full border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm bg-white transition-all duration-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10 disabled:bg-slate-50 disabled:text-slate-400 disabled:border-slate-200 placeholder:text-slate-300";
const textareaClass = "w-full border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm bg-white transition-all duration-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10 disabled:bg-slate-50 disabled:text-slate-400 disabled:border-slate-200 placeholder:text-slate-300 resize-y";
const selectClass = "w-full border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm bg-white transition-all duration-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10 disabled:bg-slate-50 disabled:border-slate-200 appearance-none pr-8";

// ─── Guided workflow metadata (stepper + sidebar dashboard) ───
const SECTION_META = [
  { id: 'search-setup', label: 'Search Setup', num: 1 },
  { id: 'tax-summary', label: 'Tax Summary', num: 2, optional: true },
  { id: 'deeds', label: 'Deeds / Chain', num: 3 },
  { id: 'vesting', label: 'Vesting', num: 4 },
  { id: 'legal', label: 'Legal Description', num: 5 },
  { id: 'dot-mortgage', label: 'DOT / Mortgage Info', num: 6, optional: true },
  { id: 'judgment-lien', label: 'Judgment / Lien Types', num: 7, optional: true },
  { id: 'names-run', label: 'Names Run', num: 8, optional: true },
  { id: 'documents', label: 'Documents', num: 9 },
  { id: 'finalize', label: 'Finalize / Complete', num: 10 },
];

// ─── Section status badges ───
// PRIMARY-DOC-UX-002: `required` renders an amber "Required" pill while incomplete (mandatory sections)
const SectionBadge = ({ complete, emptyLabel = 'Empty', doneLabel = 'Done', required = false }) => (
  complete
    ? <span className="ml-2 inline-flex items-center gap-1 text-[10.5px] font-semibold text-emerald-700 bg-emerald-50/80 border border-emerald-200/60 rounded-full px-2.5 py-0.5 tracking-wide" data-testid="section-badge-done"><CheckCircle2 className="h-3 w-3" />{doneLabel}</span>
    : required
      ? <span className="ml-2 inline-flex items-center gap-1 text-[10.5px] font-semibold text-amber-700 bg-amber-50 border border-amber-300/70 rounded-full px-2.5 py-0.5 tracking-wide" data-testid="section-badge-required"><AlertCircle className="h-3 w-3" />Required</span>
      : <span className="ml-2 inline-flex items-center gap-1 text-[10.5px] font-medium text-slate-400 bg-slate-50 border border-slate-200/60 rounded-full px-2.5 py-0.5 tracking-wide">{emptyLabel}</span>
);

// ─── Numbered step badge — turns into a checkmark when the section is complete ───
const StepBadge = ({ num, complete, required = false }) => (
  complete
    ? <span className="h-7 w-7 rounded-full bg-emerald-500 shadow-sm shadow-emerald-200/80 flex items-center justify-center flex-shrink-0 transition-colors" data-testid={`step-badge-done-${num}`}><Check className="h-4 w-4 text-white" strokeWidth={3} /></span>
    : <span className={`h-7 w-7 rounded-full bg-white border-2 flex items-center justify-center flex-shrink-0 text-[12px] font-extrabold tabular-nums sf-heading ${required ? 'border-amber-400 text-amber-700' : 'border-slate-200 text-slate-500'}`} data-testid={`step-badge-${num}`}>{num}</span>
);

// ─── Circular progress ring for the sidebar dashboard ───
const ProgressRing = ({ percent }) => {
  const r = 30;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative h-[76px] w-[76px] flex-shrink-0">
      <svg width="76" height="76" viewBox="0 0 76 76">
        <circle cx="38" cy="38" r={r} fill="none" stroke="#e2e8f0" strokeWidth="7" />
        <circle
          cx="38" cy="38" r={r} fill="none"
          stroke={percent === 100 ? '#10b981' : '#2563eb'}
          strokeWidth="7" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - percent / 100)}
          transform="rotate(-90 38 38)"
          style={{ transition: 'stroke-dashoffset 500ms ease, stroke 300ms ease' }}
        />
      </svg>
      <span className={`absolute inset-0 flex items-center justify-center text-[15px] font-extrabold tabular-nums sf-heading ${percent === 100 ? 'text-emerald-600' : 'text-blue-700'}`} data-testid="progress-ring-percent">{percent}%</span>
    </div>
  );
};

// ─── Tax Summary fields — one index-aligned record per parcel ───
const TAX_SUMMARY_FIELDS = [
  { key: 'land_value', label: 'Land Value', placeholder: 'e.g. $125,000.00' },
  { key: 'improvement_value', label: 'Improvement Value', placeholder: 'e.g. $275,000.00' },
  { key: 'total_assessed_value', label: 'Total Assessed Value', placeholder: 'Auto-calculated', readOnly: true },
];

const emptyTaxSummary = () => ({
  id: uid(),
  land_value: '',
  improvement_value: '',
  total_assessed_value: '',
  assessed_values_na: false,
  notes: '',
});

// ASSESSED-NA-001 — currency math helpers (blank ≠ $0.00; total only
// computes when BOTH inputs hold a real numeric value)
const parseCurrencyNum = (v) => {
  const s = String(v || '').trim();
  if (!s) return null;
  const n = parseFloat(s.replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? n : null;
};
const calcTotalAssessed = (land, improvement) => {
  const l = parseCurrencyNum(land);
  const m = parseCurrencyNum(improvement);
  if (l === null || m === null) return '';
  return (l + m).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const taxSummaryHasData = (record) =>
  record?.assessed_values_na === true ||
  TAX_SUMMARY_FIELDS.some(field => (record?.[field.key] || '').trim()) ||
  (record?.notes || '').trim();

// FUTURE-DATE-001 helpers now live in utils/dateGuards.js (shared with the
// Final Review modal): getBusinessToday / isFutureDate / futureDateClass / FutureDateError.

// ─── MANDATORY-FIELDS-001 (Aug 2026) — per-record required fields (matrix
// confirmed by user). Applies only when the record exists; External Portal
// orders are excluded. Mirrors collect_mandatory_field_errors in backend. ───
const MANDATORY_KEYS = {
  deed: ['grantor', 'grantee', 'deed_date', 'recorded_date', 'document_type'],
  dot_flat: ['open_end', 'original_amount', 'dated_date', 'date_recorded', 'lender', 'borrower'],
  appointed_trustees: ['appointed_trustee', 'prior_trustee', 'dated_date', 'recorded_date'],
  assignments: ['assignor', 'assignee', 'dated_date', 'recorded_date'],
  modifications: ['modified_to', 'dated_date', 'recorded_date'],
  subordination_agreements: ['borrower', 'senior_lender', 'subordinated_lender', 'dated_date', 'recorded_date'],
  legal_action: ['plaintiff', 'defendant', 'dated_date', 'recorded_date', 'judgment_type'],
};
const DOT_FLAT_LABELS = Object.fromEntries(DOT_FLAT_GROUPS.flatMap(g => g.fields.map(f => [f.key, f.label])));
const REQ_CLASS = ' !border-red-400 !ring-2 !ring-red-500/10';
// PDF-EXTRACT-001 — OCR runs as a background job; poll every 2s, give up after 6 min
const EXTRACT_POLL_MS = 2000;
const EXTRACT_POLL_MAX_MS = 6 * 60 * 1000;
const RequiredError = ({ show }) => (show
  ? <p className="mt-1 text-[11px] font-medium text-red-600" data-testid="required-field-error">Required before QC Review/Completion.</p>
  : null);
const deedHasData = (d) => !!(d.grantor || d.grantee || d.deed_date || d.recorded_date || d.book || d.page || d.instrument_number || d.document_type || d.consideration_amount || d.comments || d.cert_of_title || d.document_number);

// ─── DEED-DOCTYPE-001 (Aug 2026) — searchable Document Type dropdown ───
// ─── DATE-VALID-001 (Aug 2026) — smooth date entry + hard validation.
// Year range proposal: 1700 (colonial-era instruments appear in title
// chains) through 2100 (future maturity dates). Adjustable constants.
const DATE_MIN_YEAR = 1700;
const DATE_MAX_YEAR = 2100;
const isValidDateValue = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || '');
  if (!m) return false;
  const y = +m[1], mo = +m[2], d = +m[3];
  if (y < DATE_MIN_YEAR || y > DATE_MAX_YEAR) return false;
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
};
// DATE-VALID-002 — masked MM/DD/YYYY text entry (Effective Date). Partial or
// out-of-range input stays local and is flagged; only complete valid dates
// (years 1700–2100) are committed as ISO, so autosave never receives junk.
const isoToUs = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  return m ? `${m[2]}/${m[3]}/${m[1]}` : (iso || '');
};
const usToIso = (us) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(us || '');
  return m ? `${m[3]}-${m[1]}-${m[2]}` : '';
};
const MaskedDateText = ({ value, onCommit, testid, className, disabled, placeholder = 'MM/DD/YYYY', children }) => {
  const [local, setLocal] = useState(isoToUs(value));
  const [invalid, setInvalid] = useState(false);
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) { setLocal(isoToUs(value)); setInvalid(!!value && !isValidDateValue(value)); }
  }, [value]);
  const handle = (raw) => {
    const masked = maskDateInput(raw);
    setLocal(masked);
    if (masked === '') { setInvalid(false); if (value) onCommit(''); return; }
    const iso = usToIso(masked);
    if (iso && isValidDateValue(iso)) {
      setInvalid(false);
      if (iso !== (value || '')) onCommit(iso);
    } else {
      setInvalid(true);
    }
  };
  return (
    <>
      <div className="relative">
      <input
        type="text"
        inputMode="numeric"
        value={local}
        onFocus={() => { focusedRef.current = true; }}
        onBlur={() => { focusedRef.current = false; if (invalid && !local) setInvalid(false); }}
        onChange={e => handle(e.target.value)}
        className={`${className || inputClass}${invalid ? REQ_CLASS : ''}`}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testid}
      />
      {children}
      </div>
      {invalid && (
        <p className="text-xs text-red-600 mt-1" data-testid={`${testid}-invalid`}>
          Enter a complete, valid date (MM/DD/YYYY, years {DATE_MIN_YEAR}–{DATE_MAX_YEAR}). Not saved.
        </p>
      )}
    </>
  );
};

// Keeps keystrokes local (no panel re-render / autosave churn while typing)
// and only commits complete, valid calendar dates to the parent state.
const DateField = ({ value, onCommit, max, testid, className, disabled }) => {
  const [local, setLocal] = useState(value || '');
  const [invalid, setInvalid] = useState(false);
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) { setLocal(value || ''); setInvalid(false); }
  }, [value]);
  const handle = (v) => {
    setLocal(v);
    if (v === '' || isValidDateValue(v)) {
      setInvalid(false);
      if (v !== (value || '')) onCommit(v);
    } else {
      setInvalid(true);
    }
  };
  return (
    <>
      <input
        type="date"
        value={local}
        max={max}
        onFocus={() => { focusedRef.current = true; }}
        onBlur={() => { focusedRef.current = false; }}
        onChange={e => handle(e.target.value)}
        className={`${className || inputClass}${invalid ? REQ_CLASS : ''}`}
        data-testid={testid}
        disabled={disabled}
      />
      {invalid && (
        <p className="mt-1 text-[11px] font-medium text-red-600" data-testid={`${testid}-invalid`}>
          Enter a valid date (year {DATE_MIN_YEAR}–{DATE_MAX_YEAR}). Not saved.
        </p>
      )}
    </>
  );
};

// ─── INSTR-PANEL-001 (Aug 2026) — compact, scrollable, expandable Search
// Instructions card. Display-only: the stored instruction is never
// truncated or modified; formatting (line breaks, checklists) preserved.
const SearchInstructionsCard = ({ text, priorityNotes }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const bodyRef = useRef(null);
  useEffect(() => {
    if (!collapsed && bodyRef.current) {
      setOverflowing(bodyRef.current.scrollHeight > bodyRef.current.clientHeight + 4);
    }
  }, [text, priorityNotes, collapsed]);
  const content = (cls, testid) => (
    <p className={cls} style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }} data-testid={testid}>
      {text}
    </p>
  );
  return (
    <div className="rounded-xl border border-blue-200/80 bg-gradient-to-b from-blue-50/80 to-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden" data-testid="search-instructions-card">
      <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-100/80 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          className="sf-heading text-[11px] font-extrabold text-blue-700 uppercase tracking-wider flex items-center gap-1.5 hover:text-blue-900 transition-colors"
          data-testid="search-instructions-collapse-toggle"
          title={collapsed ? 'Expand card' : 'Collapse card'}
        >
          <FileText className="h-3 w-3" /> Search Instructions
          {collapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
        </button>
        {text && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="h-6 w-6 flex items-center justify-center rounded-md text-blue-500 hover:text-blue-800 hover:bg-blue-100 transition-colors"
            data-testid="search-instructions-expand-btn"
            title="View full instructions"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {!collapsed && (
        <>
          <div ref={bodyRef} className="px-4 py-3 max-h-[280px] overflow-y-auto overscroll-contain" data-testid="search-instructions-body">
            {text ? content('text-[12.5px] text-slate-700 leading-relaxed whitespace-pre-wrap', 'search-instructions-text')
              : <p className="text-[12px] text-slate-400 italic" data-testid="search-instructions-empty">No instructions provided for this order.</p>}
            {priorityNotes && (
              <div className="mt-2.5 pt-2.5 border-t border-blue-100/80">
                <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Priority Note</span>
                <p className="text-[12px] text-amber-700 mt-0.5 leading-relaxed" data-testid="priority-notes-text">{priorityNotes}</p>
              </div>
            )}
          </div>
          {overflowing && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="w-full px-4 py-2 text-[11.5px] font-semibold text-blue-600 hover:text-blue-800 hover:bg-blue-50 border-t border-blue-100/80 transition-colors"
              data-testid="search-instructions-view-full-btn"
            >
              View Full Instructions
            </button>
          )}
        </>
      )}
      <Dialog open={expanded} onOpenChange={(open) => { if (!open) setExpanded(false); }}>
        <DialogContent className="sm:max-w-2xl" data-testid="search-instructions-overlay">
          <DialogHeader>
            <DialogTitle className="text-sm font-extrabold text-blue-700 uppercase tracking-wider flex items-center gap-2">
              <FileText className="h-4 w-4" /> Search Instructions
            </DialogTitle>
            <DialogDescription className="sr-only">Complete client search instructions for this order.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto overscroll-contain pr-2">
            {content('text-[13.5px] text-slate-700 leading-relaxed whitespace-pre-wrap', 'search-instructions-full-text')}
            {priorityNotes && (
              <div className="mt-3 pt-3 border-t border-amber-100">
                <span className="text-[11px] font-bold text-amber-600 uppercase tracking-wider">Priority Note</span>
                <p className="text-[13px] text-amber-700 mt-1 leading-relaxed">{priorityNotes}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── RESET-RECORD-001 (Aug 2026) — per-record reset control ───
const ResetRecordButton = ({ label, disabled, onReset, testid }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onReset}
    className="h-7 w-7 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-amber-600 hover:border-amber-300 hover:bg-amber-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-sm"
    data-testid={testid}
    title={`Reset ${label}`}
  >
    <RotateCcw className="h-3.5 w-3.5" />
  </button>
);

const DEED_DOC_TYPES = [
  'Bargain and Sale Deed', 'Beneficiary Deed', 'Contract Deed', 'Conveyance Deed',
  'Court Order Deeds', 'General Warranty Deed', 'Gift Deed', 'Grant Deed',
  'Lease Deed', 'Limited Warranty Deed', 'Mortgage Deed', 'Partnership Deed',
  'Quitclaim Deed', 'Sale Deed', "Sheriff's Deed", 'Special Warranty Deed',
  'Survivorship Deed', 'Transfer-on-Death Deed', 'Trust Deed', 'Warranty Deed',
  'Will Deed', 'Fiduciary Deed', "Personal Representative's Deed",
];

const DeedDocTypeSelect = ({ value, onChange, disabled, error, testid }) => {
  // Historical free-text values (not in the list) open in "Other" mode,
  // pre-filled and untouched unless the processor edits them.
  const [otherMode, setOtherMode] = useState(() => !!(value || '').trim() && !DEED_DOC_TYPES.includes(value));
  const [open, setOpen] = useState(false);
  const isPredefined = DEED_DOC_TYPES.includes(value);
  const pick = (opt) => {
    setOpen(false);
    if (opt === 'Other') {
      setOtherMode(true);
      if (isPredefined) onChange(''); // stale predefined value never survives the switch
    } else {
      setOtherMode(false);
      onChange(opt); // replaces any manual "Other" text — stale data cleared
    }
  };
  const clear = () => {
    setOpen(false);
    setOtherMode(false);
    onChange(''); // saves as empty — nothing stale reaches the report
  };
  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={testid}
            className={`${selectClass} text-left flex items-center justify-between${error ? REQ_CLASS : ''}`}
          >
            <span className={(otherMode ? 'Other' : value) ? 'text-slate-800' : 'text-slate-300'}>
              {otherMode ? 'Other' : (value || 'Select document type…')}
            </span>
            {((value || '').trim() || otherMode) && !disabled ? (
              <span
                role="button"
                tabIndex={-1}
                onClick={(e) => { e.stopPropagation(); clear(); }}
                className="h-4 w-4 flex items-center justify-center rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                data-testid={`${testid}-clear`}
                title="Clear document type"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]" align="start">
          <Command>
            <CommandInput placeholder="Search deed types…" data-testid={`${testid}-search`} />
            <CommandList className="max-h-56">
              <CommandEmpty>No matching deed type.</CommandEmpty>
              <CommandGroup>
                {((value || '').trim() || otherMode) && (
                  <CommandItem value="__clear__" onSelect={clear} data-testid={`${testid}-opt-clear`} className="text-slate-500 italic">
                    <X className="mr-2 h-3.5 w-3.5" />
                    Clear selection
                  </CommandItem>
                )}
                {DEED_DOC_TYPES.map(opt => (
                  <CommandItem key={opt} value={opt} onSelect={() => pick(opt)} data-testid={`${testid}-opt-${opt.replace(/[^a-zA-Z]+/g, '-').toLowerCase()}`}>
                    <Check className={`mr-2 h-3.5 w-3.5 ${!otherMode && value === opt ? 'opacity-100 text-blue-600' : 'opacity-0'}`} />
                    {opt}
                  </CommandItem>
                ))}
                <CommandItem value="Other" onSelect={() => pick('Other')} data-testid={`${testid}-opt-other`}>
                  <Check className={`mr-2 h-3.5 w-3.5 ${otherMode ? 'opacity-100 text-blue-600' : 'opacity-0'}`} />
                  Other
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {otherMode && (
        <div>
          <label className="block text-[10.5px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Specify Document Type</label>
          <input
            data-testid={`${testid}-other-input`}
            className={`${inputClass}${error ? REQ_CLASS : ''}`}
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            placeholder="e.g. Personal Representative's Deed of Distribution"
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};
// Mortgage/DOT flat-field requiredness (Trustee only for Deed of Trust)
const flatFieldRequired = (field, rec) => {
  if (field.key === 'trustee') return (rec.type || '').trim() === 'Deed of Trust';
  return MANDATORY_KEYS.dot_flat.includes(field.key);
};

// ─── Empty deed row factory ───
const emptyDeed = () => ({
  id: uid(),
  grantor: '',
  grantee: '',
  deed_date: '',
  recorded_date: '',
  book: '',
  page: '',
  instrument_number: '',
  document_type: '',
  consideration_amount: '',
  comments: '',
});

// DEED-FIELD-RENAME-001 (Aug 2026): legacy deeds saved under cert_of_title /
// document_number surface in the renamed Document Type / Consideration
// Amount fields so no historical data disappears.
const normalizeDeed = (d) => {
  const { cert_of_title, document_number, ...rest } = d;
  return {
    ...rest,
    id: d.id || uid(),
    document_type: d.document_type || cert_of_title || '',
    consideration_amount: d.consideration_amount || document_number || '',
    comments: d.comments || '',
  };
};

// ─── Loading skeleton stub (module-level to satisfy react/no-unstable-nested-components) ───
const SectionSkeleton = () => (
  <div className="rounded-xl border border-slate-200/80 bg-white px-4 py-3.5">
    <div className="flex items-center gap-3">
      <div className="h-4 w-4 rounded bg-slate-200/80 animate-pulse" />
      <div className="h-3.5 w-32 rounded bg-slate-200/80 animate-pulse" />
      <div className="h-4 w-14 rounded-full bg-slate-200/60 animate-pulse ml-auto" />
    </div>
  </div>
);

// ═══════════════════════════════ MAIN COMPONENT ═══════════════════════════════
export default function SearchFulfillmentPanel({ orderData, readOnly = false, typistMode = false, currentUser = null, onOrderUpdate, onRequestSmartRedirect }) {
  const orderId = orderData?.id;
  const navigate = useNavigate();
  const { user } = useAuth();

  // ── State ──
  const [data, setData] = useState({
    parcel_ids: [{ id: uid(), value: '' }],
    tax_summaries: [emptyTaxSummary()],
    effective_date: '',
    effective_time: '',
    deeds: [emptyDeed()],
    vesting_info: '',
    legal_description: '',
    dot_mortgage_information: [emptyDotMortgage()],
    no_open_mortgage: false,
    legal_actions: [emptyLegalAction()],
    // NAMES-RUN-001 (Jun 2026) — names searched/run during the title search
    names_run: [{ id: uid(), value: '' }],
    primary_document_id: null,
    primary_document_name: null,
    misc_notes: '',
  });

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidation, setShowValidation] = useState(false);
  // V1 External Portal rule: true when the order's client uses delivery_method
  // = external_portal. Hides the Validation Errors block and shows an info
  // banner. Entry stays editable — completion just doesn't enforce validation.
  const [isExternalPortal, setIsExternalPortal] = useState(false);
  // QC-UX-001 — true when this user's submissions must go through QC Review;
  // the Complete button is hidden and QC Review is the submission action.
  const [qcReviewRequired, setQcReviewRequired] = useState(false);
  // FINAL-REVIEW-001 — two-step review modal state
  const [finalReview, setFinalReview] = useState({ open: false, action: null });
  const [finalReviewSubmitting, setFinalReviewSubmitting] = useState(false);
  const [savedChecklist, setSavedChecklist] = useState(null);
  // MANDATORY-FIELDS-001 — flips on after a blocked QC/Complete attempt so
  // every missing required field renders red with an inline message.
  const [mandatoryCheckActive, setMandatoryCheckActive] = useState(false);
  const [activeDeedTab, setActiveDeedTab] = useState(0);
  const [legalEditMode, setLegalEditMode] = useState(false);
  const [extractModalOpen, setExtractModalOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractedText, setExtractedText] = useState('');
  const [extractedFilename, setExtractedFilename] = useState('');
  const [extractMethod, setExtractMethod] = useState('');
  const [extractError, setExtractError] = useState('');
  const [extractNotice, setExtractNotice] = useState('');
  const [extractProgress, setExtractProgress] = useState(null);
  const [selectedFileId, setSelectedFileId] = useState('');
  const [openSections, setOpenSections] = useState([]);
  // NO-OPEN-MORTGAGE-003 — destructive-clear confirmation modal
  const [noOpenMortgageConfirm, setNoOpenMortgageConfirm] = useState(false);
  // TYPE-RESET-002 — { idx, newType } while awaiting confirmation
  const [typeChangeConfirm, setTypeChangeConfirm] = useState(null);
  // RESET-RECORD-001 — { label, action } while awaiting confirmation
  const [resetConfirm, setResetConfirm] = useState(null);
  const [currentSectionId, setCurrentSectionId] = useState('search-setup');
  const [activeDotTab, setActiveDotTab] = useState(0);
  // AT-TABS-001 — one visible record + numbered tabs for every repeatable
  // sub-section (assignments, modifications, appointed trustees,
  // subordinations, NOP) and Judgment/Lien entries.
  const [activeSubTabs, setActiveSubTabs] = useState({});
  const [activeLaTab, setActiveLaTab] = useState(0);
  const getSubTab = (mIdx, section, rec) => {
    const n = (rec[section.key] || []).length;
    return Math.max(0, Math.min(activeSubTabs[`${mIdx}:${section.key}`] ?? 0, n - 1));
  };
  const setSubTab = (mIdx, sectionKey, v) =>
    setActiveSubTabs(prev => ({ ...prev, [`${mIdx}:${sectionKey}`]: v }));

  const saveTimerRef = useRef(null);
  const lastSavedRef = useRef(null);
  const tokenRef = useRef(localStorage.getItem('token'));
  const vestingManuallyEditedRef = useRef(false);

  // ── Status change modal state ──
  const [statusModal, setStatusModal] = useState({ open: false, type: null }); // type: 'pending' | 'clarification'
  const [statusReason, setStatusReason] = useState('');
  const [statusChips, setStatusChips] = useState([]);
  const [statusChipsLoaded, setStatusChipsLoaded] = useState(false);
  const [selectedChipIds, setSelectedChipIds] = useState(new Set());
  const [submittingStatus, setSubmittingStatus] = useState(false);

  // ── Optimistic workflow-action state ──
  // activeAction is set SYNCHRONOUSLY on button click so the pill reflects the
  // user's choice immediately, regardless of network latency. It is cleared on
  // success or revert on failure. inflightAction tracks whether an API call is
  // in progress for that action (used to render a tiny spinner without
  // blocking the visible "selected" state).
  const [activeAction, setActiveAction] = useState(null); // 'pending' | 'clarification' | 'qc_review' | 'complete' | null
  const [inflightAction, setInflightAction] = useState(null);
  // Local mirror of orderData.status so optimistic updates reflect in the
  // top-right chip without a full page reload.
  const [localStatus, setLocalStatus] = useState(orderData?.status || null);
  useEffect(() => {
    setLocalStatus(orderData?.status || null);
  }, [orderData?.status]);

  // ── Auth header helper ──
  const authHeader = () => ({ headers: { Authorization: `Bearer ${tokenRef.current}` } });

  // ── Status change modal helpers ──
  const openStatusModal = async (type) => {
    // Paint the workflow button active IMMEDIATELY — regardless of how long
    // the chip-fetch takes. This is what makes the UI feel instant.
    setActiveAction(type);
    setStatusModal({ open: true, type });
    setStatusReason('');
    setSelectedChipIds(new Set());
    setStatusChipsLoaded(false);
    // Use search-specific chip categories for Search Fulfillment
    const chipCategory = type === 'pending' ? 'search_pending' : 'search_clarification';
    try {
      const res = await axios.get(`${API}/api/quick-chips?category=${chipCategory}`, authHeader());
      setStatusChips(res.data || []);
    } catch {
      setStatusChips([]);
    } finally {
      setStatusChipsLoaded(true);
    }
  };

  const handleChipClick = (chip) => {
    const wasSelected = selectedChipIds.has(chip.id);
    const next = new Set(selectedChipIds);
    if (wasSelected) {
      next.delete(chip.id);
    } else {
      next.add(chip.id);
    }
    setSelectedChipIds(next);
    if (!wasSelected) {
      const contextData = {
        jurisdiction_name: orderData?.property_county ? `${orderData.property_county} County` : '',
        county: orderData?.property_county || '',
        owner_name: orderData?.seller_name || orderData?.borrower_name || '',  // SELLER-NAME-001: seller = owner being searched
        parcel_apn: orderData?.parcel_apn || '',
        property_address: orderData?.property_address || '',
      };
      const resolved = applyTemplate(chip.template, contextData);
      const current = (statusReason || '').trimEnd();
      setStatusReason(current ? `${current}\n\n${resolved}` : resolved);
    }
  };

  const handleSubmitStatus = async () => {
    if (!orderId || !statusModal.type) return;
    if (statusReason.trim().length < 10) {
      toast.error('Please provide a reason (minimum 10 characters)');
      return;
    }
    setSubmittingStatus(true);
    setInflightAction(statusModal.type);
    const targetStatus = statusModal.type;
    try {
      await axios.patch(`${API}/api/orders/${orderId}/status`, {
        status: targetStatus,
        reason: statusReason.trim(),
      }, authHeader());
      toast.success(`Order moved to ${targetStatus === 'pending' ? 'Pending' : 'Clarification'} successfully`);
      setStatusModal({ open: false, type: null });
      // Optimistic local update — no full page reload. The parent can
      // refetch/invalidate its own query when ready; this keeps the UI
      // responsive in the meantime.
      setLocalStatus(targetStatus);
      setActiveAction(targetStatus);
      // Propagate upward so the parent (App.js → OrderTabs → header) can
      // update the canonical orderData.status. Without this the header's
      // StatusBadge stays on the old status.
      if (typeof onOrderUpdate === 'function') {
        onOrderUpdate({ status: targetStatus });
      }
      // Smart auto-redirect to Dashboard for Pending / Clarification.
      if (typeof onRequestSmartRedirect === 'function') {
        const label = targetStatus === 'pending' ? 'Pending' : 'Clarification';
        onRequestSmartRedirect(label);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update status');
      // Revert — the user's choice didn't land.
      setActiveAction(null);
    } finally {
      setSubmittingStatus(false);
      setInflightAction(null);
    }
  };

  // QC-UX-002 — validation failures must never be hidden inside a collapsed
  // Finalize/Complete section: expand it and scroll the error box into view.
  const revealFinalizeErrors = () => {
    setShowValidation(true);
    setOpenSections(prev => (prev.includes('finalize') ? prev : [...prev, 'finalize']));
    setTimeout(() => {
      const el = document.querySelector('[data-testid="validation-errors"]');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus?.();
      }
    }, 300);
  };

  // ── QC Review: submit through the QC workflow endpoint so the order is
  // stamped with qc_status/qc_submitted_at and lands in the QC Specialist
  // "Files Awaiting Review" queue (QC-ROUTE-001 — the previous bare status
  // PATCH skipped those fields and orders never reached the queue). ──
  const handleQCReview = async () => {
    if (!orderId) return;
    // Paint active immediately.
    setActiveAction('qc_review');
    setInflightAction('qc_review');
    try {
      await axios.post(`${API}/api/orders/${orderId}/submit-for-qc`, {
        notes: 'Sent for QC Review from search fulfillment',
      }, authHeader());
      setValidationErrors([]);
      setLocalStatus('qc_review');
      if (typeof onOrderUpdate === 'function') {
        onOrderUpdate({ status: 'qc_review' });
      }
      toast.success('Order moved to QC Review successfully');
      // TITLE-UX-REDIRECT-001 — align with the Tax workflow: after a
      // successful QC submission the processor returns to their dashboard
      // (smart 2s countdown banner, cancellable by interaction).
      if (typeof onRequestSmartRedirect === 'function') {
        onRequestSmartRedirect('QC Review');
      } else {
        setTimeout(() => navigate(getRoleDashboardPath(user), { replace: true }), 900);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to send for QC Review';
      const msgs = Array.isArray(detail) ? detail.map(d => d?.msg || String(d)) : [String(detail)];
      toast.error(msgs[0]);
      setValidationErrors(msgs);
      revealFinalizeErrors();
      setActiveAction(null);
    } finally {
      setInflightAction(null);
    }
  };

  // ── Fetch on mount ──
  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const [fulfillmentRes, filesRes] = await Promise.all([
          axios.get(`${API}/api/orders/${orderId}/search-fulfillment`, authHeader()),
          axios.get(`${API}/api/orders/${orderId}/files`, authHeader()),
        ]);

        if (cancelled) return;

        const f = fulfillmentRes.data.fulfillment || {};
        // Convert parcel_ids: legacy string → array, new array → array with ids
        let parsedParcels = [{ id: uid(), value: '' }];
        const rawParcels = f.parcel_ids;
        if (Array.isArray(rawParcels) && rawParcels.length > 0) {
          parsedParcels = rawParcels.map(v => ({ id: uid(), value: typeof v === 'string' ? v : '' }));
        } else if (typeof rawParcels === 'string' && rawParcels.trim()) {
          parsedParcels = [{ id: uid(), value: rawParcels.trim() }];
        } else if (!rawParcels || (Array.isArray(rawParcels) && rawParcels.length === 0)) {
          // Pre-fill from order data if no saved parcels
          const fallback = orderData?.parcel_apn || orderData?.property?.parcel_id || '';
          parsedParcels = [{ id: uid(), value: fallback }];
        }
        setData(prev => ({
          ...prev,
          parcel_ids: parsedParcels,
          // TAX-SUMMARY-001 — parallel array, index-aligned with parcels
          tax_summaries: parsedParcels.map((_, i) => {
            const raw = Array.isArray(f.tax_summaries) ? f.tax_summaries[i] : null;
            return raw ? { ...emptyTaxSummary(), ...raw, id: raw.id || uid() } : emptyTaxSummary();
          }),
          effective_date: f.effective_date || '',
          effective_time: f.effective_time || '',
          deeds: f.deeds?.length ? f.deeds.map(normalizeDeed) : [emptyDeed()],
          vesting_info: f.vesting_info || '',
          legal_description: f.legal_description || '',
          dot_mortgage_information: f.dot_mortgage_information?.length
            ? f.dot_mortgage_information.map(normalizeDotMortgage)
            : [emptyDotMortgage()],
          // NO-OPEN-MORTGAGE-001 — existing orders without the flag load as false
          no_open_mortgage: f.no_open_mortgage === true,
          // JUDGMENT-LIEN-001 — load saved entries, or lift legacy
          // per-mortgage Legal Action data into the new top-level array.
          legal_actions: (() => {
            if (Array.isArray(f.legal_actions) && f.legal_actions.length) {
              return f.legal_actions.map(la => ({ ...emptyLegalAction(), ...la, id: la.id || uid() }));
            }
            const lifted = (f.dot_mortgage_information || []).map(liftLegacyLegalAction).filter(Boolean);
            return lifted.length ? lifted : [emptyLegalAction()];
          })(),
          primary_document_id: f.primary_document_id || null,
          primary_document_name: f.primary_document_name || null,
          misc_notes: f.misc_notes || '',
          // NAMES-RUN-001 — stored as plain strings; edited as {id, value} rows
          names_run: (Array.isArray(f.names_run) && f.names_run.length)
            ? f.names_run.map(v => ({ id: uid(), value: v || '' }))
            : [{ id: uid(), value: '' }],
        }));

        // TS-FILENAME-001: a delivered Final Search Report is never a merge input
        setFiles((filesRes.data.files || []).filter(x => !x.is_final_search_report && x.file_category !== 'final_search_report'));
        setIsExternalPortal(!!fulfillmentRes.data.is_external_portal);
        setQcReviewRequired(!!fulfillmentRes.data.qc_review_required);
        setSavedChecklist(fulfillmentRes.data.final_review?.checklist || null);
        lastSavedRef.current = JSON.stringify(f);
        // SECTION-COLLAPSE-001 — computed ONCE on load: sections holding
        // saved data open automatically; empty sections stay collapsed.
        // Never re-computed while the processor works (no auto-collapse).
        const opened = [];
        if ((f.parcel_ids || []).length || f.effective_date || f.effective_time) opened.push('search-setup');
        if ((f.tax_summaries || []).some(ts => Object.entries(ts || {}).some(([k, v]) => k !== 'id' && (v || '').toString().trim()))) opened.push('tax-summary');
        if ((f.deeds || []).length) opened.push('deeds');
        if ((f.vesting_info || '').trim() || (f.estate_type || '').trim()) opened.push('vesting');
        if ((f.legal_description || '').trim()) opened.push('legal');
        if (f.no_open_mortgage === true || (f.dot_mortgage_information || []).length) opened.push('dot-mortgage');
        if ((f.legal_actions || []).length) opened.push('judgment-lien');
        if ((f.names_run || []).some(n => (n || '').trim())) opened.push('names-run');
        if (f.primary_document_id || (filesRes.data.files || []).length) opened.push('documents');
        setOpenSections(opened);
        if (opened.length) setCurrentSectionId(opened[0]);
        // If backend already has vesting text, treat as manually edited to avoid overwriting
        vestingManuallyEditedRef.current = !!(f.vesting_info?.trim());
        // If legal description already exists, stay in preview mode (not edit)
        setLegalEditMode(false);
      } catch (err) {
        console.error('Failed to load search fulfillment data', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [orderId]);

  // Feb 2026 — Live external-portal config sync (lightweight; matches the
  // TaxCertificatePanel fix). On window focus / visibility-change we
  // re-fetch ONLY the external-portal flag — not the whole fulfillment
  // payload — so an admin toggling Client Code delivery_method in
  // another tab is reflected without a hard refresh AND without
  // clobbering the user's unsaved form state in this panel.
  //
  // Feb 2026 v2 — Same hardening as TaxCertificatePanel:
  //   • AbortController cancels prior in-flight EP fetches so only the
  //     freshest result wins (eliminates the "(pending)" stuck request
  //     that left state stale).
  //   • pageshow only acts on bfcache restores (event.persisted), not
  //     on the initial-mount fire that browsers send.
  const epAbortRef = useRef(null);
  useEffect(() => {
    if (!orderId) return undefined;
    const refreshEPOnly = async () => {
      try {
        if (epAbortRef.current) {
          try { epAbortRef.current.abort(); } catch { /* noop */ }
        }
        const controller = new AbortController();
        epAbortRef.current = controller;
        const token = localStorage.getItem('token');
        const resp = await axios.get(
          `${API}/api/orders/${orderId}/external-portal-status`,
          { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
        );
        if (epAbortRef.current !== controller) return;
        setIsExternalPortal(!!resp.data?.is_external_portal);
      } catch (err) {
        // Silent — aborted or transient. Don't surface a toast.
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshEPOnly();
    };
    const onFocus = () => refreshEPOnly();
    const onPageShow = (ev) => { if (ev?.persisted) refreshEPOnly(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      if (epAbortRef.current) {
        try { epAbortRef.current.abort(); } catch { /* noop */ }
      }
    };
  }, [orderId]);

  // ── Auto-save with debounce ──
  const triggerAutoSave = useCallback(() => {
    if (readOnly || !orderId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(async () => {
      const payload = buildPayload();

      const serialized = JSON.stringify(payload);
      if (serialized === lastSavedRef.current) return;

      try {
        setSaving(true);
        await axios.post(
          `${API}/api/orders/${orderId}/search-fulfillment`,
          { fulfillment: payload },
          authHeader(),
        );
        lastSavedRef.current = serialized;
      } catch (err) {
        console.error('Auto-save failed', err);
      } finally {
        setSaving(false);
      }
    }, 1200);
  }, [data, orderId, readOnly]);

  useEffect(() => {
    if (!loading) triggerAutoSave();
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [data, triggerAutoSave, loading]);

  // ── Generic updater ──
  const update = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  // ── Parcel ID helpers ──
  const updateParcel = (idx, value) => {
    setData(prev => {
      const parcels = [...prev.parcel_ids];
      parcels[idx] = { ...parcels[idx], value };
      return { ...prev, parcel_ids: parcels };
    });
  };
  const addParcel = () => setData(prev => ({
    ...prev,
    parcel_ids: [...prev.parcel_ids, { id: uid(), value: '' }],
    tax_summaries: [...prev.tax_summaries, emptyTaxSummary()],
  }));
  const removeParcel = () => setData(prev => {
    if (prev.parcel_ids.length <= 1) return prev;
    return {
      ...prev,
      parcel_ids: prev.parcel_ids.slice(0, -1),
      tax_summaries: prev.tax_summaries.slice(0, -1),
    };
  });

  // ── Tax Summary helper (index-aligned with parcel_ids) ──
  const updateTaxSummary = (idx, field, value) => {
    setData(prev => {
      const list = [...prev.tax_summaries];
      while (list.length <= idx) list.push(emptyTaxSummary());
      const next = { ...list[idx], [field]: value };
      // ASSESSED-NA-001 — Total is always derived, never typed
      if (field === 'land_value' || field === 'improvement_value') {
        next.total_assessed_value = calcTotalAssessed(next.land_value, next.improvement_value);
      }
      list[idx] = next;
      return { ...prev, tax_summaries: list };
    });
  };

  // ASSESSED-NA-001 — toggle "N/A – Assessed Values Not Available"
  const toggleAssessedNA = (idx, checked) => {
    const ts = data.tax_summaries[idx] || emptyTaxSummary();
    if (checked) {
      const hasValues = ['land_value', 'improvement_value', 'total_assessed_value'].some(k => (ts[k] || '').trim());
      if (hasValues && !window.confirm('Assessed values have already been entered. Selecting N/A will clear these values. Continue?')) return;
    }
    setData(prev => {
      const list = [...prev.tax_summaries];
      while (list.length <= idx) list.push(emptyTaxSummary());
      list[idx] = checked
        ? { ...list[idx], assessed_values_na: true, land_value: '', improvement_value: '', total_assessed_value: '' }
        : { ...list[idx], assessed_values_na: false };
      return { ...prev, tax_summaries: list };
    });
  };

  // ── Deed helpers ──
  const VESTING_TRIGGER_FIELDS = new Set(['grantee', 'deed_date', 'recorded_date', 'book', 'page', 'instrument_number']);
  const updateDeed = (idx, field, value) => {
    setData(prev => {
      const deeds = [...prev.deeds];
      deeds[idx] = { ...deeds[idx], [field]: value };
      // Trigger auto-vesting update for relevant fields
      if (VESTING_TRIGGER_FIELDS.has(field)) {
        // Use setTimeout(0) to run after state has settled
        setTimeout(() => maybeAutoUpdateVesting(deeds), 0);
      }
      return { ...prev, deeds };
    });
  };
  const addDeed = () => {
    setData(prev => ({ ...prev, deeds: [...prev.deeds, emptyDeed()] }));
    setActiveDeedTab(data.deeds.length); // Switch to newly added tab
  };
  const removeDeed = (idx) => {
    if (data.deeds.length <= 1) return;
    setData(prev => ({
      ...prev,
      deeds: prev.deeds.filter((_, i) => i !== idx),
    }));
    setActiveDeedTab(prev => {
      if (idx <= prev && prev > 0) return prev - 1;
      if (prev >= data.deeds.length - 1) return data.deeds.length - 2;
      return prev;
    });
  };

  // ── DOT / Mortgage helpers ──
  // REQUIRED-AMOUNT-001 (Jun 2026) — single qualifying mortgage whose
  // Original Amount flows to Final Review / PDF as "Required Amount".
  const setRequiredAmountMortgage = (mIdx, checked) => {
    if (checked && !((data.dot_mortgage_information[mIdx]?.original_amount || '').trim())) {
      toast.error('Enter the Original Amount before selecting this mortgage as the Required Amount source');
      return;
    }
    const prevIdx = data.dot_mortgage_information.findIndex((m, i) => m.use_for_required_amount === true && i !== mIdx);
    setData(prev => ({
      ...prev,
      dot_mortgage_information: prev.dot_mortgage_information.map((m, i) => ({
        ...m,
        use_for_required_amount: i === mIdx ? checked : false,
      })),
    }));
    if (checked) {
      const amt = (data.dot_mortgage_information[mIdx].original_amount || '').trim();
      toast.success(prevIdx >= 0
        ? `Mortgage #${mIdx + 1} is now the Required Amount source (Mortgage #${prevIdx + 1} deselected) — Required Amount: ${amt}`
        : `Mortgage #${mIdx + 1} set as the Required Amount source — Required Amount: ${amt}`);
    }
  };

  const updateDotSub = (mIdx, sectionKey, subIdx, field, value) => {
    setData(prev => {
      const records = [...prev.dot_mortgage_information];
      const subs = [...(records[mIdx][sectionKey] || [])];
      subs[subIdx] = { ...subs[subIdx], [field]: value };
      records[mIdx] = { ...records[mIdx], [sectionKey]: subs };
      if (sectionKey === 'assignments' && field === 'assignee') {
        records[mIdx] = syncAssignmentChain(records[mIdx], prev.dot_mortgage_information[mIdx]);
      }
      return { ...prev, dot_mortgage_information: records };
    });
  };
  // ASSIGN-OVERRIDE-003 — any manual typing into Assignor pauses auto-fill for
  // that record until the processor explicitly resets it back to AUTO.
  const setAssignorManual = (mIdx, subIdx, value) => {
    setData(prev => {
      const records = [...prev.dot_mortgage_information];
      const subs = [...(records[mIdx].assignments || [])];
      subs[subIdx] = { ...subs[subIdx], assignor: value, assignor_linked: false, assignor_overridden: true };
      records[mIdx] = { ...records[mIdx], assignments: subs };
      return { ...prev, dot_mortgage_information: records };
    });
  };
  const resetAssignorAuto = (mIdx, subIdx) => {
    setData(prev => {
      const records = [...prev.dot_mortgage_information];
      const subs = [...(records[mIdx].assignments || [])];
      subs[subIdx] = { ...subs[subIdx], assignor_overridden: false, assignor_linked: false };
      records[mIdx] = syncAssignmentChain({ ...records[mIdx], assignments: subs });
      return { ...prev, dot_mortgage_information: records };
    });
  };
  const addDotSub = (mIdx, section) => {
    setData(prev => {
      const records = [...prev.dot_mortgage_information];
      records[mIdx] = { ...records[mIdx], [section.key]: [...(records[mIdx][section.key] || []), emptySubRecord(section)] };
      if (section.key === 'assignments') records[mIdx] = syncAssignmentChain(records[mIdx]);
      return { ...prev, dot_mortgage_information: records };
    });
    setSubTab(mIdx, section.key, (data.dot_mortgage_information[mIdx]?.[section.key] || []).length);
  };
  const removeDotSub = (mIdx, section, subIdx) => {
    setData(prev => {
      const records = [...prev.dot_mortgage_information];
      const subs = (records[mIdx][section.key] || []).filter((_, i) => i !== subIdx);
      records[mIdx] = { ...records[mIdx], [section.key]: subs.length ? subs : [emptySubRecord(section)] };
      if (section.key === 'assignments') records[mIdx] = syncAssignmentChain(records[mIdx]);
      return { ...prev, dot_mortgage_information: records };
    });
    setActiveSubTabs(prev => {
      const k = `${mIdx}:${section.key}`;
      const cur = prev[k] ?? 0;
      return { ...prev, [k]: subIdx <= cur && cur > 0 ? cur - 1 : cur };
    });
  };
  const updateDotMortgage = (idx, field, value) => {
    // TYPE-RESET-002 — changing/clearing the Type wipes the record's data;
    // confirm first when anything would be lost.
    if (field === 'type') {
      const rec = data.dot_mortgage_information[idx];
      if ((rec.type || '') !== (value || '') && mortgageRecordHasClearableData(rec)) {
        setTypeChangeConfirm({ idx, newType: value });
        return;
      }
    }
    setData(prev => {
      const records = [...prev.dot_mortgage_information];
      const next = { ...records[idx], [field]: value };
      // REQUIRED-AMOUNT-001 — clearing the Original Amount auto-deselects
      // the qualifying-mortgage flag (a blank amount can never be the source).
      if (field === 'original_amount' && !(value || '').trim() && next.use_for_required_amount === true) {
        next.use_for_required_amount = false;
        toast.info(`Mortgage #${idx + 1} deselected as the Required Amount source — Original Amount was cleared`);
      }
      if (field === 'type' && value !== 'Deed of Trust') next.trustee = '';
      records[idx] = field === 'lender' ? syncAssignmentChain(next, records[idx]) : next;
      return { ...prev, dot_mortgage_information: records };
    });
  };
  const applyTypeChange = () => {
    const { idx, newType } = typeChangeConfirm;
    setData(prev => {
      const records = [...prev.dot_mortgage_information];
      records[idx] = { ...emptyDotMortgage(), id: records[idx].id, type: newType, comments: records[idx].comments || '' };
      return { ...prev, dot_mortgage_information: records };
    });
    setActiveSubTabs({});
    setTypeChangeConfirm(null);
  };
  const addDotMortgage = () => {
    setData(prev => ({ ...prev, dot_mortgage_information: [...prev.dot_mortgage_information, emptyDotMortgage()] }));
    setActiveDotTab(data.dot_mortgage_information.length);
  };
  const removeDotMortgage = (idx) => {
    if (data.dot_mortgage_information.length <= 1) {
      setData(prev => ({ ...prev, dot_mortgage_information: [emptyDotMortgage()] }));
      setActiveDotTab(0);
      return;
    }
    setData(prev => ({
      ...prev,
      dot_mortgage_information: prev.dot_mortgage_information.filter((_, i) => i !== idx),
    }));
    setActiveDotTab(prev => {
      if (idx <= prev && prev > 0) return prev - 1;
      if (prev >= data.dot_mortgage_information.length - 1) return data.dot_mortgage_information.length - 2;
      return prev;
    });
  };

  // ── Judgment / Lien Type helpers (JUDGMENT-LIEN-001) ──
  const updateLegalAction = (idx, field, value) => {
    setData(prev => {
      const list = [...prev.legal_actions];
      list[idx] = { ...list[idx], [field]: value };
      return { ...prev, legal_actions: list };
    });
  };
  const addLegalAction = () => {
    setData(prev => ({ ...prev, legal_actions: [...prev.legal_actions, emptyLegalAction()] }));
    setActiveLaTab(data.legal_actions.length);
  };

  // ── NAMES-RUN-001 helpers ──
  const updateNameRun = (idx, value) => setData(prev => {
    const list = [...prev.names_run];
    list[idx] = { ...list[idx], value };
    return { ...prev, names_run: list };
  });
  const addNameRun = () => setData(prev => ({ ...prev, names_run: [...prev.names_run, { id: uid(), value: '' }] }));
  const removeNameRun = (idx) => setData(prev => ({
    ...prev,
    names_run: prev.names_run.length <= 1 ? [{ id: uid(), value: '' }] : prev.names_run.filter((_, i) => i !== idx),
  }));

  // ── NO-OPEN-MORTGAGE-003 — destructive clear behind a confirmation ──
  const mortgageRecordHasClearableData = (m) =>
    DOT_FLAT_KEYS.some(k => k !== 'type' && (m[k] || '').trim()) ||
    DOT_SUB_SECTIONS.some(s => (m[s.key] || []).some(sub => subRecordHasData(s, sub)));
  const mortgageHasClearableData = () =>
    data.dot_mortgage_information.some(mortgageRecordHasClearableData);
  const applyNoOpenMortgage = () => {
    setData(prev => {
      const mergedComments = prev.dot_mortgage_information
        .map(m => (m.comments || '').trim()).filter(Boolean).join('\n');
      return {
        ...prev,
        no_open_mortgage: true,
        dot_mortgage_information: [{ ...emptyDotMortgage(), comments: mergedComments }],
      };
    });
    setActiveDotTab(0);
    setNoOpenMortgageConfirm(false);
  };
  const handleNoOpenMortgageToggle = (checked) => {
    if (!checked) { update('no_open_mortgage', false); return; }
    if (mortgageHasClearableData()) { setNoOpenMortgageConfirm(true); return; }
    applyNoOpenMortgage();
  };

  // ── RESET-RECORD-001 — per-record resets (only the chosen record) ──
  const requestReset = (label, action) => setResetConfirm({ label, action });
  const resetDeed = (idx) => setData(prev => {
    const deeds = [...prev.deeds];
    deeds[idx] = { ...emptyDeed(), id: deeds[idx].id };
    return { ...prev, deeds };
  });
  const resetDotMortgage = (idx) => setData(prev => {
    const records = [...prev.dot_mortgage_information];
    records[idx] = { ...emptyDotMortgage(), id: records[idx].id };
    return { ...prev, dot_mortgage_information: records };
  });
  const resetDotSub = (mIdx, section, subIdx) => setData(prev => {
    const records = [...prev.dot_mortgage_information];
    const subs = [...(records[mIdx][section.key] || [])];
    subs[subIdx] = { ...emptySubRecord(section), id: subs[subIdx].id };
    records[mIdx] = { ...records[mIdx], [section.key]: subs };
    if (section.key === 'assignments') records[mIdx] = syncAssignmentChain(records[mIdx], prev.dot_mortgage_information[mIdx]);
    return { ...prev, dot_mortgage_information: records };
  });
  const resetLegalAction = (idx) => setData(prev => {
    const list = [...prev.legal_actions];
    list[idx] = { ...emptyLegalAction(), id: list[idx].id };
    return { ...prev, legal_actions: list };
  });
  const resetTaxSummary = (idx) => setData(prev => {
    const ts = [...prev.tax_summaries];
    ts[idx] = { ...ts[idx], land_value: '', improvement_value: '', total_assessed_value: '', assessed_values_na: false, notes: '' };
    return { ...prev, tax_summaries: ts };
  });

  // ── FUTURE-DATE-001 — collect every future-date violation with a section/field label ──
  const collectFutureDateViolations = () => {
    const errs = [];
    const chk = (val, label) => { if (isFutureDate(val)) errs.push(`Future date not allowed — ${label}`); };
    chk(data.effective_date, 'Search Setup: Effective Date');
    data.deeds.forEach((d, i) => {
      chk(d.deed_date, `Deeds / Chain of Title #${i + 1}: Date of Deed`);
      chk(d.recorded_date, `Deeds / Chain of Title #${i + 1}: Recorded Date`);
    });
    // NO-OPEN-MORTGAGE-001 — disabled mortgage fields never block completion
    if (!data.no_open_mortgage) data.dot_mortgage_information.forEach((m, i) => {
      const p = `Mortgage/DOT #${i + 1}`;
      DOT_FLAT_GROUPS.forEach(g => g.fields.filter(noFuture).forEach(f => chk(m[f.key], `${p}: ${f.label}`)));
      DOT_SUB_SECTIONS.forEach(s => (m[s.key] || []).forEach((sub, j) => {
        s.fields.filter(noFuture).forEach(f => chk(sub[f.key], `${p} — ${s.title} #${j + 1}: ${f.label}`));
      }));
    });
    data.legal_actions.forEach((la, i) => {
      LEGAL_ACTION_FIELDS.filter(f => f.kind === 'date').forEach(f => chk(la[f.key], `Judgment / Lien Type #${i + 1}: ${f.label}`));
    });
    return errs;
  };

  // ── Canonical save payload (shared by autosave / final review / complete) ──
  const buildPayload = () => ({
    ...data,
    parcel_ids: data.parcel_ids.map(p => p.value).filter(v => v.trim()),
    // TAX-SUMMARY-001 — keep summaries index-aligned with the FILTERED
    // parcel list so Parcel #N ↔ Tax Summary #N never desync on reload.
    tax_summaries: data.parcel_ids
      .map((p, i) => ({ v: p.value, ts: data.tax_summaries[i] || emptyTaxSummary() }))
      .filter(pr => pr.v.trim())
      .map(pr => pr.ts),
    deeds: data.deeds.filter(d => d.grantor || d.grantee || d.deed_date || d.recorded_date || d.book || d.page || d.instrument_number || d.document_type || d.consideration_amount || d.comments || d.cert_of_title || d.document_number),
    dot_mortgage_information: sanitizeDotRecords(data.dot_mortgage_information),
    legal_actions: data.legal_actions.filter(legalActionHasData),
    names_run: data.names_run.map(n => (n.value || '').trim()).filter(Boolean),
  });

  // ── MANDATORY-FIELDS-001 — collect per-record missing required fields ──
  const collectMandatoryFieldViolations = () => {
    if (isExternalPortal) return [];
    const errors = [];
    const blank = (v) => !(v || '').toString().trim();
    const req = (rec, key, label) => { if (blank(rec[key])) errors.push(`${label} — Required before QC Review/Completion`); };
    data.deeds.forEach((d, i) => {
      if (!deedHasData(d)) return;
      const p = `Deeds / Chain of Title #${i + 1}`;
      req(d, 'grantor', `${p}: Grantor`);
      req(d, 'grantee', `${p}: Grantee`);
      req(d, 'deed_date', `${p}: Date of Deed`);
      req(d, 'recorded_date', `${p}: Recorded Date`);
      req(d, 'document_type', `${p}: Document Type`);
    });
    // NO-OPEN-MORTGAGE-001 — disabled mortgage fields are not required
    if (!data.no_open_mortgage) data.dot_mortgage_information.forEach((m, i) => {
      if (!dotMortgageHasData(m)) return;
      const p = `Mortgage/DOT #${i + 1}`;
      MANDATORY_KEYS.dot_flat.forEach(k => req(m, k, `${p}: ${DOT_FLAT_LABELS[k] || k}`));
      if ((m.type || '').trim() === 'Deed of Trust') req(m, 'trustee', `${p}: Trustee`);
      DOT_SUB_SECTIONS.forEach(s => (m[s.key] || []).forEach((sub, j) => {
        if (!subRecordHasData(s, sub)) return;
        (MANDATORY_KEYS[s.key] || []).forEach(k => {
          const f = s.fields.find(x => x.key === k);
          req(sub, k, `${p} — ${s.title} #${j + 1}: ${f ? f.label : k}`);
        });
      }));
    });
    data.legal_actions.forEach((la, i) => {
      if (!legalActionHasData(la)) return;
      const p = `Judgment / Lien Type #${i + 1}`;
      MANDATORY_KEYS.legal_action.forEach(k => {
        const f = LEGAL_ACTION_FIELDS.find(x => x.key === k);
        req(la, k, `${p}: ${f ? f.label : k}`);
      });
    });
    return errors;
  };

  // ── FINAL-REVIEW-001 — mandatory checklist + report review before QC/Complete ──
  const openFinalReview = async (action) => {
    // PRIMARY-DOC-001 (Aug 28 2026) — the Primary Search Document selection
    // is mandatory before direct Complete or QC Review submission.
    if (!isExternalPortal && !data.primary_document_id) {
      toast.error('Please select a Primary Search Document before completing or submitting this order for QC Review.');
      setOpenSections(prev => (prev.includes('documents') ? prev : [...prev, 'documents']));
      setTimeout(() => document.querySelector('[data-testid="primary-document-select"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
      return;
    }
    // FINAL-REVIEW-003 (Aug 28 2026, user decision): the Final Review
    // checklist is now mandatory on direct Complete too (reverses the
    // SEARCH-COMPLETE-002 checklist skip). Field-level pre-gates remain
    // QC-Review-only — Complete keeps its supporting-docs-only field gating.
    // MANDATORY-FIELDS-001 + FUTURE-DATE-001 pre-gates (QC Review only)
    const mandErrors = action === 'qc_review' ? collectMandatoryFieldViolations() : [];
    const futureDateErrors = action === 'qc_review' ? collectFutureDateViolations() : [];
    // DATE-VALID-002 — a malformed Effective Date (legacy records) blocks both paths
    if (data.effective_date && !isValidDateValue(data.effective_date)) {
      futureDateErrors.push(`Search Setup: Effective Date '${data.effective_date}' is not a valid date (MM/DD/YYYY, years ${DATE_MIN_YEAR}–${DATE_MAX_YEAR})`);
    }
    if (mandErrors.length > 0 || futureDateErrors.length > 0) {
      setMandatoryCheckActive(true);
      setValidationErrors([...mandErrors, ...futureDateErrors]);
      setShowValidation(true);
      toast.error(mandErrors.length > 0
        ? 'Required fields are missing — complete the highlighted fields before QC Review/Completion'
        : 'Please correct the highlighted date fields (invalid or future dates are not allowed)');
      // Open all sections, then scroll to the first highlighted field
      setOpenSections(['search-setup', 'tax-summary', 'deeds', 'vesting', 'legal', 'dot-mortgage', 'judgment-lien', 'names-run', 'documents', 'finalize']);
      setTimeout(() => {
        const el = document.querySelector('[data-testid="required-field-error"], [data-testid="future-date-error"], [data-testid="effective-date-input-invalid"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      return;
    }
    try {
      // Force-save so the submitted fulfillment is exactly what's on screen.
      await axios.post(`${API}/api/orders/${orderId}/search-fulfillment`, { fulfillment: buildPayload() }, authHeader());
    } catch (err) {
      // DUAL-ROLE-002 — surface the server's reason (e.g. QC scope / Primary Document lock) instead of a generic toast
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail ? detail : 'Failed to save fulfillment before review');
      return;
    }
    // SEARCH-FLOW-002 (Aug 2026): superseded for COMPLETE by the
    // SEARCH-COMPLETE-001 early-exit above; QC Review submissions keep the
    // Final Review step unchanged.
    setFinalReview({ open: true, action });
  };

  const handleFinalReviewConfirm = async (checklist) => {
    setFinalReviewSubmitting(true);
    try {
      // FINAL-REVIEW-PDF-001 — persist anything typed inside the modal (e.g.
      // Miscellaneous/Additional Notes) BEFORE acknowledging, so the ack hash
      // matches the saved fulfillment and the notes reach the final PDF.
      try {
        await axios.post(`${API}/api/orders/${orderId}/search-fulfillment`, { fulfillment: buildPayload() }, authHeader());
      } catch {
        toast.error('Failed to save fulfillment before acknowledgment');
        return;
      }
      const res = await axios.post(`${API}/api/orders/${orderId}/search-fulfillment/final-review`, {
        checklist,
        checklist_acknowledged: true,
        report_acknowledged: true,
      }, authHeader());
      if (res.data.status !== 'acknowledged') {
        toast.error((res.data.errors || ['Final Review validation failed'])[0]);
        return;
      }
      setSavedChecklist(checklist);
      const action = finalReview.action;
      setFinalReview({ open: false, action: null });
      if (action === 'qc_review') await handleQCReview();
      else await handleComplete();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record Final Review acknowledgment');
    } finally {
      setFinalReviewSubmitting(false);
    }
  };
  const removeLegalAction = (idx) => {
    setData(prev => {
      const list = prev.legal_actions.filter((_, i) => i !== idx);
      return { ...prev, legal_actions: list.length ? list : [emptyLegalAction()] };
    });
    setActiveLaTab(prev => (idx <= prev && prev > 0 ? prev - 1 : prev));
  };

  // ── Build rich vesting text from deed + order context ──
  // VESTING-DEED-001 (Sep 2026): Title Vesting derives from deed #1 — the
  // tab explicitly labeled "Vesting". Never the last array item, never
  // date-based selection; the processor controls the Vesting position.
  const buildVestingText = useCallback((deeds) => {
    const deed = (deeds || [])[0];
    if (!deed?.grantee?.trim()) return '';

    const grantee = deed.grantee.trim();
    const parts = [];

    // Deed date
    if (deed.deed_date) {
      try {
        const d = parse(deed.deed_date, 'yyyy-MM-dd', new Date());
        if (isValid(d)) parts.push(`dated ${format(d, 'MM/dd/yyyy')}`);
      } catch { /* skip */ }
    }

    // Recorded date
    if (deed.recorded_date) {
      try {
        const d = parse(deed.recorded_date, 'yyyy-MM-dd', new Date());
        if (isValid(d)) parts.push(`recorded ${format(d, 'MM/dd/yyyy')}`);
      } catch { /* skip */ }
    }

    // Book & Page
    if (deed.book?.trim() && deed.page?.trim()) {
      parts.push(`in Book ${deed.book.trim()}, Page ${deed.page.trim()}`);
    }

    // Instrument #
    if (deed.instrument_number?.trim()) {
      parts.push(`Instrument #${deed.instrument_number.trim()}`);
    }

    // County from order context
    const county = orderData?.property?.county || orderData?.county || '';
    const recorderSuffix = county ? ` with ${county.replace(/\s+county$/i, '')} County Recorder` : '';

    if (parts.length > 0) {
      return `${grantee} by deed ${parts.join(', ')}${recorderSuffix}.`;
    }
    return `Title is vested in ${grantee}${recorderSuffix ? ` by deed recorded${recorderSuffix}` : ''}.`;
  }, [orderData]);

  // ── Auto-generate vesting (button action — always regenerates) ──
  const autoGenerateVesting = () => {
    const text = buildVestingText(data.deeds);
    if (text) {
      update('vesting_info', text);
      vestingManuallyEditedRef.current = false;
    }
  };

  // VESTING-DEED-001: informational-only hint when a later deed in the chain
  // is recorded more recently than the Vesting deed (#1). Recorded Date is
  // primary; Date of Deed is the fallback ONLY for this comparison. Strictly
  // later dates only (equal dates never warn). Never blocks Save/Complete,
  // never changes deed selection or the generated vesting text.
  const newerDeedWarning = useMemo(() => {
    const deeds = data.deeds || [];
    if (deeds.length < 2) return null;
    const effDate = (d) => {
      const raw = (d?.recorded_date || d?.deed_date || '').trim();
      if (!raw) return null;
      const p = parse(raw, 'yyyy-MM-dd', new Date());
      return isValid(p) ? p : null;
    };
    const vestingDate = effDate(deeds[0]);
    if (!vestingDate) return null;
    let newest = null;
    deeds.slice(1).forEach((d, i) => {
      const dt = effDate(d);
      if (dt && dt > vestingDate && (!newest || dt > newest.dt)) {
        newest = { num: i + 2, dt };
      }
    });
    if (!newest) return null;
    return `Deed #${newest.num} is recorded more recently than the Vesting deed. Please verify that the Vesting tab contains the current vesting deed.`;
  }, [data.deeds]);

  // ── Reactive auto-update: regenerate vesting when deed fields change (only if not manually edited) ──
  const maybeAutoUpdateVesting = useCallback((updatedDeeds) => {
    if (vestingManuallyEditedRef.current) return;
    const text = buildVestingText(updatedDeeds);
    // Only set if there's meaningful text to avoid blanking the field
    if (text) {
      setData(prev => ({ ...prev, vesting_info: text }));
    }
  }, [buildVestingText]);

  // ── PDF Extraction handlers ──
  const openExtractModal = () => {
    setExtractedText('');
    setExtractedFilename('');
    setExtractMethod('');
    setExtractError('');
    setExtractNotice('');
    setSelectedFileId('');
    setExtractModalOpen(true);
  };

  const applyExtractResult = (d) => {
    const text = (d.text || '').trim();
    if (!text) {
      setExtractError('No readable text was found in this PDF.');
      return;
    }
    setExtractedText(text);
    setExtractedFilename(d.filename || '');
    setExtractMethod(d.method || '');
    setExtractNotice(d.notice || '');
  };

  const pollExtractJob = async (jobId) => {
    const started = Date.now();
    while (Date.now() - started < EXTRACT_POLL_MAX_MS) {
      await new Promise(r => setTimeout(r, EXTRACT_POLL_MS));
      const res = await axios.get(`${API}/api/orders/${orderId}/extract-pdf-text/${jobId}`, authHeader());
      const d = res.data || {};
      if (d.progress) setExtractProgress(d.progress);
      if (d.status === 'done') { applyExtractResult(d); return; }
      if (d.status === 'failed') { setExtractError(d.error || 'Extraction failed.'); return; }
    }
    setExtractError('Extraction is taking longer than expected. Please retry, or use a smaller PDF.');
  };

  const handleExtract = async () => {
    if (!selectedFileId) return;
    setExtracting(true);
    setExtractedText('');
    setExtractError('');
    setExtractNotice('');
    setExtractProgress(null);
    try {
      const res = await axios.post(
        `${API}/api/orders/${orderId}/extract-pdf-text`,
        { file_id: selectedFileId },
        authHeader()
      );
      if (res.status === 202 && res.data?.job_id) {
        setExtractProgress(res.data.progress || null);
        await pollExtractJob(res.data.job_id);
      } else {
        applyExtractResult(res.data || {});
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setExtractError(
        typeof detail === 'string' && detail
          ? detail
          : err?.response ? `Extraction failed (HTTP ${err.response.status}).` : 'Could not reach the server. Check your connection and try again.'
      );
    } finally {
      setExtracting(false);
    }
  };

  const applyExtractedText = (mode) => {
    if (mode === 'use' || mode === 'replace') {
      update('legal_description', extractedText);
    } else if (mode === 'append') {
      update('legal_description', (data.legal_description ? data.legal_description + '\n\n' : '') + extractedText);
    }
    setExtractModalOpen(false);
    setLegalEditMode(true);
  };

  const pdfFiles = files.filter(f =>
    (f.content_type || '').toLowerCase().includes('pdf') ||
    (f.filename || '').toLowerCase().endsWith('.pdf')
  );

  // ── Completion handler ──
  const handleComplete = async () => {
    if (!orderId) return;
    // Paint Complete active IMMEDIATELY — pre-save latency is what the user
    // was feeling; the button state now flips on click, not on response.
    setActiveAction('complete');
    setCompleting(true);
    setInflightAction('complete');
    setShowValidation(true);

    // SEARCH-COMPLETE-002: client future-date gate removed for Complete —
    // the only completion gate is Supporting Documents (server-enforced).
    // QC Review pre-gates (incl. future dates) remain in openFinalReview.
    try {
      // Force-save first
      const payload = buildPayload();
      await axios.post(`${API}/api/orders/${orderId}/search-fulfillment`, { fulfillment: payload }, authHeader());

      const res = await axios.post(`${API}/api/orders/${orderId}/search-complete`, {}, authHeader());

      if (res.data.status === 'validation_failed') {
        const errs = res.data.errors || [];
        setValidationErrors(errs);
        // ORDER-COMPLETION-VALIDATION-001: show the actual reasons (not a generic line)
        toast.error(
          <div>
            <strong>Cannot complete order:</strong>
            <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px' }}>
              {errs.slice(0, 5).map((m, i) => <li key={i} style={{ fontSize: '12px' }}>{m}</li>)}
              {errs.length > 5 && <li style={{ fontSize: '12px', fontStyle: 'italic' }}>+{errs.length - 5} more issues</li>}
              {errs.length === 0 && <li style={{ fontSize: '12px' }}>Please resolve validation issues before completing</li>}
            </ul>
          </div>,
          { duration: 8000 }
        );
        revealFinalizeErrors();
        // Validation failed — no status change actually happened; release the
        // button back so the user can correct and retry.
        setActiveAction(null);
      } else if (res.data.status === 'qc_required') {
        // TYPING-001 — requires_qc processors cannot bypass QC
        setValidationErrors(res.data.errors || []);
        toast.error((res.data.errors || ['QC review is required — use the QC Review button'])[0]);
        revealFinalizeErrors();
        setActiveAction(null);
      } else if (res.data.status === 'error') {
        setValidationErrors(res.data.errors || []);
        toast.error((res.data.errors || ['Unable to complete this order'])[0]);
        revealFinalizeErrors();
        setActiveAction(null);
      } else if (res.data.status === 'routed_to_typing') {
        // TYPING-001 — search work done; order now in Typing queue
        setValidationErrors([]);
        setLocalStatus('typing');
        if (typeof onOrderUpdate === 'function') {
          onOrderUpdate({ status: 'typing' });
        }
        toast.success('Search work completed — order moved to the Typing queue');
        setTimeout(() => {
          navigate(getRoleDashboardPath(user), { replace: true });
        }, 900);
      } else if (res.data.status === 'completed') {
        setValidationErrors([]);
        setLocalStatus('completed');
        if (typeof onOrderUpdate === 'function') {
          onOrderUpdate({ status: 'completed' });
        }
        toast.success('Order completed successfully');

        // Feb 27, 2026 — BUG FIX: `navigate(-1)` sent the user back to
        // whatever page was previously in history, which was typically
        // the Files tab (since processors upload documents before
        // clicking Complete on Fulfillment). User-facing symptom: after
        // Complete they'd see the Files tab of the just-completed
        // order instead of their dashboard.
        //
        // Fix: always route to the role-appropriate dashboard using the
        // shared `getRoleDashboardPath()` util (same mapping as
        // App.js::getDefaultRoute). Uses `replace: true` so the
        // completed order doesn't remain in back-button history.
        setTimeout(() => {
          navigate(getRoleDashboardPath(user), { replace: true });
        }, 900);
      }
    } catch (err) {
      console.error('Completion failed', err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to complete search order', { duration: 8000 });
      setActiveAction(null);
    } finally {
      setCompleting(false);
      setInflightAction(null);
    }
  };

  // ── Section completeness checks ──
  const isSetupComplete = data.parcel_ids.some(p => p.value?.trim()) && data.effective_date?.trim();
  const isDeedsComplete = data.deeds.some(d => d.grantor?.trim() || d.grantee?.trim());
  const isVestingComplete = !!data.vesting_info?.trim();
  const isLegalComplete = !!data.legal_description?.trim();
  const isDotMortgageComplete = data.no_open_mortgage === true || data.dot_mortgage_information.some(dotMortgageHasData);
  const isJudgmentLienComplete = data.legal_actions.some(legalActionHasData);
  const isTaxSummaryComplete = data.tax_summaries.some(taxSummaryHasData);
  const isDocsComplete = !!data.primary_document_id;
  // PRIMARY-DOC-UX-002 — completion readiness surfaced in the action bar (backend gates remain the final safeguard)
  const primaryDocMissing = !isExternalPortal && !data.primary_document_id;
  const PRIMARY_DOC_BLOCK_COMPLETE = 'Primary Search Document required — upload and select the completed search document before completing this order.';
  const PRIMARY_DOC_BLOCK_QC = 'Primary Search Document required — upload and select the completed search document before submitting this order for QC Review.';
  const guideToDocuments = () => {
    setOpenSections(prev => (prev.includes('documents') ? prev : [...prev, 'documents']));
    setTimeout(() => (document.querySelector('[data-testid="primary-document-select"], [data-testid="primary-document-required-callout"]') || document.querySelector('[data-testid="documents-trigger"]'))?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
  };

  // ── Loading state ──
  // Feb 25, 2026: replaced the full-page centered spinner with a
  // layout-matching skeleton so processors see the panel outline
  // (header bar, progress placeholder, 8 accordion section stubs,
  // right-side summary column) immediately on tab click. Prevents
  // the "blank screen for 2-3 seconds" UX complaint. The skeleton
  // is swapped in-place for the real sections once `loading` flips.
  if (loading) {
    return (
      <div className="relative pb-20 sf-premium" data-testid="search-fulfillment-panel">
        {/* Header skeleton — mirrors the blue gradient bar exactly */}
        <div
          className="rounded-xl overflow-hidden border border-slate-200/80 mb-5"
          data-testid="search-fulfillment-loading"
        >
          <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600 px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="h-8 w-8 rounded-lg bg-white/15 flex items-center justify-center">
                <Search className="h-4 w-4 text-white" />
              </span>
              <h2 className="sf-heading text-base font-extrabold text-white tracking-tight">Search Fulfillment</h2>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-blue-100 bg-white/10 border border-white/20 rounded-full px-3 py-0.5">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading…
              </span>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="w-28 h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full w-1/4 rounded-full bg-white/40 animate-pulse" />
              </div>
              <span className="text-[11px] font-semibold text-white/70 tabular-nums">—</span>
            </div>
          </div>
        </div>

        {/* Two-column skeleton matching final layout */}
        <div className="flex gap-5">
          <div className="flex-1 min-w-0 space-y-3.5">
            {/* 9 section stubs — matches the 9 accordion sections */}
            <SectionSkeleton />
            <SectionSkeleton />
            <SectionSkeleton />
            <SectionSkeleton />
            <SectionSkeleton />
            <SectionSkeleton />
            <SectionSkeleton />
            <SectionSkeleton />
            <SectionSkeleton />
          </div>

          {/* Right summary column skeleton (hidden on small screens) */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="rounded-xl border border-slate-200/80 bg-white p-4 space-y-3 sticky top-4">
              <div className="h-3 w-20 rounded bg-slate-200/80 animate-pulse" />
              <div className="space-y-2 pt-2">
                <div className="h-2 w-full rounded bg-slate-100 animate-pulse" />
                <div className="h-2 w-11/12 rounded bg-slate-100 animate-pulse" />
                <div className="h-2 w-10/12 rounded bg-slate-100 animate-pulse" />
                <div className="h-2 w-9/12 rounded bg-slate-100 animate-pulse" />
                <div className="h-2 w-11/12 rounded bg-slate-100 animate-pulse" />
                <div className="h-2 w-8/12 rounded bg-slate-100 animate-pulse" />
              </div>
              <div className="pt-3 border-t border-slate-100">
                <div className="h-8 w-full rounded-lg bg-slate-100 animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isCompleted = localStatus === 'completed';
  // TYPING-EDIT-001 — External Title Search + typist → every field locked
  // except Parcel ID(s). Internal + typist → full edit (typingRestricted false).
  const typingRestricted = typistMode && isExternalPortal;
  const isDisabled = readOnly || isCompleted || typingRestricted;
  const parcelEditable = !readOnly && !isCompleted;
  
  // ── Progress calculation ──
  const completionChecks = [
    isSetupComplete,
    isDeedsComplete,
    isVestingComplete,
    isLegalComplete,
  ];
  const completedCount = completionChecks.filter(Boolean).length;
  const completionPercent = Math.round((completedCount / completionChecks.length) * 100);

  // ── Sidebar dashboard helpers ──
  const sectionOkMap = {
    'search-setup': isSetupComplete,
    'tax-summary': isTaxSummaryComplete,
    deeds: isDeedsComplete,
    vesting: isVestingComplete,
    legal: isLegalComplete,
    'dot-mortgage': isDotMortgageComplete,
    'judgment-lien': isJudgmentLienComplete,
    'names-run': data.names_run.some(n => (n.value || '').trim()),
    documents: isDocsComplete,
    finalize: completionPercent === 100,
  };
  const currentSectionLabel = SECTION_META.find(s => s.id === currentSectionId)?.label
    || (currentSectionId === 'documents' ? 'Documents' : currentSectionId === 'finalize' ? 'Finalize / Complete' : '—');
  const goToSection = (id) => {
    setOpenSections(prev => (prev.includes(id) ? prev : [...prev, id]));
    setCurrentSectionId(id);
    setTimeout(() => {
      document.querySelector(`[data-testid="${id}-trigger"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
  };

  // ═══════════════════════════════ RENDER ═══════════════════════════════
  return (
    <div className="relative pb-20 sf-premium" data-testid="search-fulfillment-panel">
      {/* ── Header bar — TITLEFLOW-FULFILLMENT-UI-001 premium gradient header ── */}
      <div className="sf-header px-5 py-4 mb-5 flex items-center justify-between gap-4" data-testid="sf-header">
        <div className="relative flex items-center gap-4 min-w-0">
          <span className="sf-header-icon flex-shrink-0"><Search className="h-5 w-5 text-white" /></span>
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="sf-heading text-[19px] font-extrabold text-white tracking-tight leading-tight">Search Fulfillment</h2>
              <span className="sf-status-pill" data-tone={isCompleted ? 'done' : 'active'} data-testid="sf-header-status">
                {isCompleted ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="h-1.5 w-1.5 rounded-full bg-white/90" />}
                {isCompleted ? 'Completed' : (localStatus === 'qc_review' ? 'QC Review' : localStatus === 'clarification' ? 'Clarification' : 'In Progress')}
              </span>
            </div>
            <p className="text-[13px] text-blue-100/90 mt-0.5 truncate">Process and verify the details used for this order fulfillment</p>
          </div>
        </div>
        <div className="relative flex items-center gap-5 flex-shrink-0 lg:pr-24">
          <div className="flex flex-col items-end gap-1.5">
            {saving ? (
              <span className="inline-flex items-center gap-1.5 text-[12px] text-blue-100 font-medium"><Loader2 className="h-3 w-3 animate-spin" /> Saving...</span>
            ) : !loading && (
              <span className="inline-flex items-center gap-2 text-[12px] text-blue-50 font-medium" data-testid="sf-autosaved"><span className="sf-dot" /> Auto-saved</span>
            )}
            <div className="flex items-center gap-3">
              <div className="sf-progress" data-done={completionPercent === 100} role="progressbar" aria-valuenow={completionPercent} aria-valuemin={0} aria-valuemax={100}>
                <span style={{ width: `${completionPercent}%` }} />
              </div>
              <span className="sf-heading text-[15px] font-extrabold text-white tabular-nums" data-testid="sf-header-percent">{completionPercent}%</span>
            </div>
          </div>
          <span className="sf-header-art hidden lg:flex h-12 w-12 rounded-xl bg-white/15 border border-white/20 items-center justify-center" aria-hidden="true"><FileText className="h-6 w-6 text-white/90" /></span>
        </div>
      </div>

      {/* V1 External Portal banner (Feb 2026). Shown at the top of the
          Search Fulfillment panel for any order whose Client Code has
          delivery_method = external_portal. Completion endpoint skips
          validation for these orders (see routes/search_fulfillment.py).
          Inputs stay editable — entry is optional, not forbidden. */}
      {isExternalPortal && !isCompleted && (
        <div
          className="mb-5 border border-blue-200 bg-blue-50 rounded-xl px-4 py-3 flex items-start gap-3"
          data-testid="external-portal-info-banner"
        >
          <span className="h-7 w-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Search className="h-3.5 w-3.5 text-blue-700" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-blue-900">External Portal Search Order</p>
            <p className="text-[12px] text-blue-800 mt-0.5" data-testid={typistMode ? 'typing-external-parcel-only-note' : undefined}>
              {typistMode
                ? 'External Title Search — fulfillment fields are read-only for Typing. Parcel ID(s) remain editable.'
                : 'This is an external portal Search order — fulfillment data is maintained in the client\u2019s portal. Entry is optional for V1. At least one supporting document (and source evidence) is still required to complete.'}
            </p>
          </div>
        </div>
      )}

      {/* ── Two-column layout: workflow sections (left) + summary panel (right) ── */}
      <div className="rounded-2xl bg-slate-100/70 border border-slate-200/70 p-3.5 sm:p-4" data-testid="workspace-canvas">
      <div className="flex gap-5">
        {/* ─── Left: Workflow Sections ─── */}
        <div className="flex-1 min-w-0">
          <Accordion
            type="multiple"
            value={openSections}
            onValueChange={(vals) => {
              const added = vals.find(v => !openSections.includes(v));
              if (added) setCurrentSectionId(added);
              setOpenSections(vals);
            }}
            className="space-y-3.5"
          >

        {/* ═══════════ 1. Search Setup ═══════════ */}
        <AccordionItem data-sf-card="true" value="search-setup" className="sf-card border border-slate-200/80 rounded-xl bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden transition-shadow duration-200 data-[state=open]:border-l-4 data-[state=open]:border-l-blue-500 data-[state=open]:ring-1 data-[state=open]:ring-blue-100 data-[state=open]:shadow-[0_6px_20px_-8px_rgba(37,99,235,0.18)]">
          <AccordionTrigger className="sf-trigger px-5 py-4 hover:no-underline hover:bg-slate-50/60 transition-colors" data-testid="search-setup-trigger">
            <div className="flex items-center gap-2.5">
              <StepBadge num={1} complete={isSetupComplete} />
              <MapPin className="h-4 w-4 text-blue-500 flex-shrink-0" />
              <span className="flex flex-col"><span className="sf-heading sf-sec-title">Search Setup</span><span className="sf-sec-desc">Review and verify the search details for this order</span></span>
              <SectionBadge complete={isSetupComplete} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="sf-content px-5 pb-5">
            <div className="space-y-4">
              {/* TITLE-PRICING-DESIGN-001 — explicit Search Method / Full Search Depth capture + coverage guidance */}
              <FulfillmentSummary orderId={orderId} order={orderData} readOnly={isDisabled} onSaved={() => onOrderUpdate && onOrderUpdate()} />
              {/* Parcel ID rows */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-medium text-slate-600">Parcel ID(s)</label>
                  {parcelEditable && (
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={removeParcel}
                        disabled={data.parcel_ids.length <= 1}
                        className="h-7 w-7 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-300 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-sm"
                        data-testid="remove-parcel-btn"
                        title="Remove last Parcel ID"
                      >
                        <span className="text-base font-bold leading-none">-</span>
                      </button>
                      <button
                        onClick={addParcel}
                        className="h-7 w-7 flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
                        data-testid="add-parcel-btn"
                        title="Add Parcel ID"
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  {data.parcel_ids.map((parcel, idx) => (
                    <div key={parcel.id} className="flex items-center gap-2" data-testid={`parcel-row-${idx}`}>
                      <span className="flex-shrink-0 text-[11px] font-medium text-slate-400 w-20">Parcel #{idx + 1}</span>
                      <input
                        data-testid={`parcel-id-input-${idx}`}
                        type="text"
                        className={`flex-1 ${inputClass}`}
                        value={parcel.value}
                        onChange={e => updateParcel(idx, e.target.value)}
                        placeholder="e.g. 1234-56-789-00"
                        disabled={!parcelEditable}
                      />
                    </div>
                  ))}
                </div>
              </div>
              {/* Effective Date & Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-slate-600">Effective Date</label>
                  {!isDisabled && (
                    <button
                      onClick={() => update('effective_date', format(new Date(), 'yyyy-MM-dd'))}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
                      data-testid="set-current-date-btn"
                    >
                      Set to Current Date
                    </button>
                  )}
                </div>
                <Popover>
                    <MaskedDateText
                      testid="effective-date-input"
                      className={`${inputClass} pr-9${futureDateClass(data.effective_date)}`}
                      value={data.effective_date}
                      onCommit={v => update('effective_date', v)}
                      disabled={isDisabled}
                    >
                    <PopoverTrigger asChild disabled={isDisabled}>
                      <button
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition-colors disabled:hover:text-slate-400"
                        data-testid="calendar-trigger-btn"
                        disabled={isDisabled}
                        type="button"
                      >
                        <Calendar className="h-4 w-4" />
                      </button>
                    </PopoverTrigger>
                    </MaskedDateText>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={data.effective_date ? (() => { try { const d = parse(data.effective_date, 'yyyy-MM-dd', new Date()); return isValid(d) ? d : undefined; } catch { return undefined; } })() : undefined}
                      onSelect={(day) => {
                        if (day) {
                          update('effective_date', format(day, 'yyyy-MM-dd'));
                        }
                      }}
                      disabled={{ after: new Date() }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FutureDateError value={data.effective_date} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-slate-600">Effective Time</label>
                  {!isDisabled && (
                    <button
                      onClick={() => update('effective_time', format(new Date(), 'HH:mm'))}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
                      data-testid="set-current-time-btn"
                    >
                      Set Current Time
                    </button>
                  )}
                </div>
                <div className="relative">
                  <input
                    data-testid="effective-time-input"
                    type="time"
                    className={`${inputClass} pr-9`}
                    value={data.effective_time}
                    onChange={e => update('effective_time', e.target.value)}
                    disabled={isDisabled}
                  />
                  <button
                    onClick={() => { if (!isDisabled) update('effective_time', format(new Date(), 'HH:mm')); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition-colors"
                    data-testid="clock-trigger-btn"
                    type="button"
                    disabled={isDisabled}
                    title="Set current time"
                  >
                    <Clock className="h-4 w-4" />
                  </button>
                </div>
              </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ═══════════ 2. Tax Summary ═══════════ */}
        <AccordionItem data-sf-card="true" value="tax-summary" className="sf-card border border-slate-200/80 rounded-xl bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden transition-shadow duration-200 data-[state=open]:border-l-4 data-[state=open]:border-l-blue-500 data-[state=open]:ring-1 data-[state=open]:ring-blue-100 data-[state=open]:shadow-[0_6px_20px_-8px_rgba(37,99,235,0.18)]">
          <AccordionTrigger className="sf-trigger px-5 py-4 hover:no-underline hover:bg-slate-50/60 transition-colors" data-testid="tax-summary-trigger">
            <div className="flex items-center gap-2.5">
              <StepBadge num={2} complete={isTaxSummaryComplete} />
              <Receipt className="h-4 w-4 text-teal-500 flex-shrink-0" />
              <span className="flex flex-col"><span className="sf-heading sf-sec-title">Tax Summary</span><span className="sf-sec-desc">Assessed values and tax notes per parcel</span></span>
              <span className="text-[11px] text-slate-400 font-medium">({data.parcel_ids.length} parcel{data.parcel_ids.length === 1 ? '' : 's'})</span>
              <SectionBadge complete={isTaxSummaryComplete} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="sf-content px-5 pb-5">
            <p className="text-xs text-slate-500 mb-4">
              One Tax Summary per Parcel ID — entries are created automatically from the parcels in Search Setup and stay in sync with them.
            </p>
            <div className="space-y-3">
              {data.parcel_ids.map((parcel, idx) => {
                const ts = data.tax_summaries[idx] || emptyTaxSummary();
                return (
                  <div key={parcel.id} className="border border-slate-200 rounded-lg p-4 bg-white shadow-sm transition-shadow hover:shadow-md" data-testid={`tax-summary-record-${idx}`}>
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Tax Summary — Parcel #{idx + 1}</span>
                      <span
                        className={`text-[11px] font-mono font-semibold rounded px-2 py-0.5 border max-w-[50%] truncate ${
                          (parcel.value || '').trim()
                            ? 'text-blue-700 bg-blue-50 border-blue-100'
                            : 'text-slate-400 bg-slate-50 border-slate-200 italic'
                        }`}
                        data-testid={`tax-summary-parcel-id-${idx}`}
                        title={(parcel.value || '').trim() || 'No Parcel ID entered yet'}
                      >
                        {(parcel.value || '').trim() || 'No Parcel ID entered yet'}
                      </span>
                      {!isDisabled && (
                        <ResetRecordButton
                          label={`Tax Summary #${idx + 1}`}
                          disabled={!taxSummaryHasData(ts)}
                          onReset={() => requestReset(`Tax Summary #${idx + 1}`, () => resetTaxSummary(idx))}
                          testid={`reset-tax-summary-btn-${idx}`}
                        />
                      )}
                    </div>
                    {/* ASSESSED-NA-001 — N/A flag (distinct from $0.00) */}
                    <label className="flex items-center gap-2 mb-3 cursor-pointer select-none w-fit">
                      <input
                        type="checkbox"
                        data-testid={`tax-summary-na-checkbox-${idx}`}
                        className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        checked={ts.assessed_values_na === true}
                        onChange={e => toggleAssessedNA(idx, e.target.checked)}
                        disabled={isDisabled}
                      />
                      <span className="text-[12px] font-medium text-slate-600">N/A – Assessed Values Not Available</span>
                    </label>
                    {ts.assessed_values_na === true && (
                      <div className="mb-3 text-[12px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid={`tax-summary-na-note-${idx}`}>
                        Assessed Values not available!
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {TAX_SUMMARY_FIELDS.map(field => (
                        <div key={field.key}>
                          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                            {field.label}
                            {field.readOnly && <span className="ml-1 normal-case font-normal text-slate-400">(auto)</span>}
                          </label>
                          <input
                            data-testid={`tax-summary-${field.key}-${idx}`}
                            className={`${inputClass} ${field.readOnly ? 'bg-slate-50 text-slate-600' : ''}`}
                            value={ts[field.key] || ''}
                            onChange={field.readOnly ? undefined : (e => updateTaxSummary(idx, field.key, e.target.value))}
                            onBlur={(!field.readOnly && isCurrency(field)) ? (e => updateTaxSummary(idx, field.key, formatUSCurrency(e.target.value))) : undefined}
                            placeholder={ts.assessed_values_na === true ? 'N/A' : field.placeholder}
                            disabled={isDisabled || ts.assessed_values_na === true}
                            readOnly={field.readOnly === true}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="mt-3">
                      <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Notes</label>
                      <textarea
                        data-testid={`tax-summary-notes-${idx}`}
                        className={`${inputClass} min-h-[80px] resize-y`}
                        rows={3}
                        value={ts.notes || ''}
                        onChange={e => updateTaxSummary(idx, 'notes', e.target.value)}
                        onBlur={e => updateTaxSummary(idx, 'notes', autoCommentsCase(e.target.value))}
                        placeholder="Free-form notes for this parcel's tax summary..."
                        disabled={isDisabled}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ═══════════ 3. Deeds / Chain of Title ═══════════ */}
        <AccordionItem data-sf-card="true" value="deeds" className="sf-card border border-slate-200/80 rounded-xl bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden transition-shadow duration-200 data-[state=open]:border-l-4 data-[state=open]:border-l-blue-500 data-[state=open]:ring-1 data-[state=open]:ring-blue-100 data-[state=open]:shadow-[0_6px_20px_-8px_rgba(37,99,235,0.18)]">
          <AccordionTrigger className="sf-trigger px-5 py-4 hover:no-underline hover:bg-slate-50/60 transition-colors" data-testid="deeds-trigger">
            <div className="flex items-center gap-2.5">
              <StepBadge num={3} complete={isDeedsComplete} />
              <ScrollText className="h-4 w-4 text-indigo-500 flex-shrink-0" />
              <span className="flex flex-col"><span className="sf-heading sf-sec-title">Deeds / Chain of Title</span><span className="sf-sec-desc">Record the conveyances that establish the chain of title</span></span>
              <span className="text-[11px] text-slate-400 font-medium">({data.deeds.filter(d => d.grantor || d.grantee).length} entries)</span>
              <SectionBadge complete={isDeedsComplete} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="sf-content px-5 pb-5">
            <p className="text-xs text-slate-500 mb-4">
              For registered land enter a document type and consideration amount, otherwise enter a book, page and instrument number.
            </p>

            {/* ── Tabbed Deed Card ── */}
            <div className="border border-slate-200/80 rounded-xl overflow-hidden bg-slate-50/50" data-testid="deeds-tabbed-card">
              {/* Tab bar + add/remove buttons */}
              <div className="flex items-center bg-slate-100/70 border-b border-slate-200/80">
                <div className="flex items-center flex-1 overflow-x-auto">
                  {data.deeds.map((deed, idx) => (
                    <button
                      key={deed.id}
                      type="button"
                      onClick={() => setActiveDeedTab(idx)}
                      className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-r border-slate-200/80 transition-all duration-200 ${
                        activeDeedTab === idx
                          ? 'bg-white text-slate-800 shadow-sm border-b-2 border-b-blue-500'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50/80'
                      }`}
                      data-testid={`deed-tab-${idx}`}
                    >
                      {idx === 0 ? 'Vesting' : `#${idx + 1}`}
                    </button>
                  ))}
                </div>
                {!isDisabled && (
                  <div className="flex items-center gap-0.5 px-2 flex-shrink-0">
                    <ResetRecordButton
                      label={`Deed #${activeDeedTab + 1}`}
                      disabled={!deedHasData(data.deeds[activeDeedTab] || {})}
                      onReset={() => requestReset(`Deed #${activeDeedTab + 1}`, () => resetDeed(activeDeedTab))}
                      testid="reset-deed-btn"
                    />
                    <button
                      type="button"
                      onClick={() => removeDeed(activeDeedTab)}
                      disabled={data.deeds.length <= 1}
                      className="h-7 w-7 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-300 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-sm"
                      data-testid="remove-deed-btn"
                      title="Remove current deed tab"
                    >
                      <span className="text-base font-bold leading-none">-</span>
                    </button>
                    <button
                      type="button"
                      onClick={addDeed}
                      className="h-7 w-7 flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
                      data-testid="add-deed-btn"
                      title="Add new deed entry"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>

              {/* Active deed form card */}
              {data.deeds[activeDeedTab] && (() => {
                const deed = data.deeds[activeDeedTab];
                const idx = activeDeedTab;
                return (
                  <div className="p-5 bg-white" data-testid={`deed-form-${idx}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      {/* ── Left Column: Grantor & Grantee ── */}
                      <div className="space-y-4">
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Grantor</label>
                          <textarea
                            data-testid={`deed-grantor-${idx}`}
                            className={`${textareaClass} min-h-[90px]${mandatoryCheckActive && !isExternalPortal && deedHasData(deed) && !(deed.grantor || '').trim() ? REQ_CLASS : ''}`}
                            value={deed.grantor}
                            onChange={e => updateDeed(idx, 'grantor', e.target.value)}
                            onBlur={e => updateDeed(idx, 'grantor', autoPartyCase(e.target.value))}
                            placeholder="Enter grantor name(s)..."
                            disabled={isDisabled}
                          />
                          <RequiredError show={mandatoryCheckActive && !isExternalPortal && deedHasData(deed) && !(deed.grantor || '').trim()} />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Grantee</label>
                          <textarea
                            data-testid={`deed-grantee-${idx}`}
                            className={`${textareaClass} min-h-[90px]${mandatoryCheckActive && !isExternalPortal && deedHasData(deed) && !(deed.grantee || '').trim() ? REQ_CLASS : ''}`}
                            value={deed.grantee}
                            onChange={e => updateDeed(idx, 'grantee', e.target.value)}
                            onBlur={e => updateDeed(idx, 'grantee', autoPartyCase(e.target.value))}
                            placeholder="Enter grantee name(s)..."
                            disabled={isDisabled}
                          />
                          <RequiredError show={mandatoryCheckActive && !isExternalPortal && deedHasData(deed) && !(deed.grantee || '').trim()} />
                        </div>
                      </div>

                      {/* ── Right Column: Dates, Book/Page/Instrument, OR, Cert/Doc ── */}
                      <div className="space-y-4">
                        {/* Date of Deed & Recorded Date */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Date of Deed</label>
                            <DateField
                              max={getBusinessToday()}
                              testid={`deed-date-${idx}`}
                              className={`${inputClass}${futureDateClass(deed.deed_date)}${mandatoryCheckActive && !isExternalPortal && deedHasData(deed) && !(deed.deed_date || '').trim() ? REQ_CLASS : ''}`}
                              value={deed.deed_date}
                              onCommit={v => updateDeed(idx, 'deed_date', v)}
                              disabled={isDisabled}
                            />
                            <FutureDateError value={deed.deed_date} />
                            <RequiredError show={mandatoryCheckActive && !isExternalPortal && deedHasData(deed) && !(deed.deed_date || '').trim()} />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Recorded Date</label>
                            <DateField
                              max={getBusinessToday()}
                              testid={`deed-recorded-date-${idx}`}
                              className={`${inputClass}${futureDateClass(deed.recorded_date)}${mandatoryCheckActive && !isExternalPortal && deedHasData(deed) && !(deed.recorded_date || '').trim() ? REQ_CLASS : ''}`}
                              value={deed.recorded_date}
                              onCommit={v => updateDeed(idx, 'recorded_date', v)}
                              disabled={isDisabled}
                            />
                            <FutureDateError value={deed.recorded_date} />
                            <RequiredError show={mandatoryCheckActive && !isExternalPortal && deedHasData(deed) && !(deed.recorded_date || '').trim()} />
                          </div>
                        </div>

                        {/* Book, Page, Instrument # */}
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Book</label>
                            <input
                              data-testid={`deed-book-${idx}`}
                              className={inputClass}
                              value={deed.book}
                              onChange={e => updateDeed(idx, 'book', e.target.value)}
                              placeholder="Book"
                              disabled={isDisabled}
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Page</label>
                            <input
                              data-testid={`deed-page-${idx}`}
                              className={inputClass}
                              value={deed.page}
                              onChange={e => updateDeed(idx, 'page', e.target.value)}
                              placeholder="Page"
                              disabled={isDisabled}
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Instrument #</label>
                            <input
                              data-testid={`deed-instrument-${idx}`}
                              className={inputClass}
                              value={deed.instrument_number}
                              onChange={e => updateDeed(idx, 'instrument_number', e.target.value)}
                              placeholder="Instrument"
                              disabled={isDisabled}
                            />
                          </div>
                        </div>

                        {/* Document Type & Consideration Amount (DEED-FIELD-RENAME-001) */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Document Type</label>
                            <DeedDocTypeSelect
                              key={deed.id}
                              value={deed.document_type || ''}
                              onChange={(v) => updateDeed(idx, 'document_type', v)}
                              disabled={isDisabled}
                              error={mandatoryCheckActive && !isExternalPortal && deedHasData(deed) && !(deed.document_type || '').trim()}
                              testid={`deed-document-type-${idx}`}
                            />
                            <RequiredError show={mandatoryCheckActive && !isExternalPortal && deedHasData(deed) && !(deed.document_type || '').trim()} />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Consideration Amount</label>
                            <input
                              data-testid={`deed-consideration-amount-${idx}`}
                              className={inputClass}
                              value={deed.consideration_amount || ''}
                              onChange={e => updateDeed(idx, 'consideration_amount', e.target.value)}
                              onBlur={e => updateDeed(idx, 'consideration_amount', formatUSCurrency(e.target.value))}
                              placeholder="e.g. $250,000"
                              disabled={isDisabled}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Comments — full width below both columns */}
                    <div className="mt-4">
                      <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Comments</label>
                      <textarea
                        data-testid={`deed-comments-${idx}`}
                        className={`${inputClass} min-h-[96px] resize-y`}
                        rows={3}
                        value={deed.comments || ''}
                        onChange={e => updateDeed(idx, 'comments', e.target.value)}
                        onBlur={e => updateDeed(idx, 'comments', autoCommentsCase(e.target.value))}
                        placeholder="Free-form notes for this deed/document..."
                        disabled={isDisabled}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ═══════════ 4. Title Vesting ═══════════ */}
        <AccordionItem data-sf-card="true" value="vesting" className="sf-card border border-slate-200/80 rounded-xl bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden transition-shadow duration-200 data-[state=open]:border-l-4 data-[state=open]:border-l-blue-500 data-[state=open]:ring-1 data-[state=open]:ring-blue-100 data-[state=open]:shadow-[0_6px_20px_-8px_rgba(37,99,235,0.18)]">
          <AccordionTrigger className="sf-trigger px-5 py-4 hover:no-underline hover:bg-slate-50/60 transition-colors" data-testid="vesting-trigger">
            <div className="flex items-center gap-2.5">
              <StepBadge num={4} complete={isVestingComplete} />
              <Landmark className="h-4 w-4 text-amber-500 flex-shrink-0" />
              <span className="flex flex-col"><span className="sf-heading sf-sec-title">Title Vesting</span><span className="sf-sec-desc">How title is currently held</span></span>
              <SectionBadge complete={isVestingComplete} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="sf-content px-5 pb-5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-slate-600">Vesting Information</label>
                {!vestingManuallyEditedRef.current && data.vesting_info && (
                  <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded" data-testid="vesting-auto-badge">Auto-synced</span>
                )}
              </div>
              {!isDisabled && (
                <button
                  onClick={autoGenerateVesting}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
                  data-testid="auto-vesting-btn"
                >
                  {vestingManuallyEditedRef.current ? 'Regenerate from deed' : 'Auto-generate from deed'}
                </button>
              )}
            </div>
            {newerDeedWarning && (
              <div
                className="flex items-start gap-2 mb-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs"
                data-testid="vesting-newer-deed-warning"
              >
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-amber-500" />
                <span>{newerDeedWarning}</span>
              </div>
            )}
            <textarea
              data-testid="vesting-input"
              className={`${textareaClass} min-h-[80px]`}
              value={data.vesting_info}
              onChange={e => {
                update('vesting_info', e.target.value);
                vestingManuallyEditedRef.current = true;
              }}
              onBlur={e => update('vesting_info', autoPartyCase(e.target.value.trim()))}
              placeholder="e.g. Title is vested in John Smith and Jane Smith, husband and wife, as tenants by the entirety"
              disabled={isDisabled}
            />
          </AccordionContent>
        </AccordionItem>

        {/* ═══════════ 5. Legal Description ═══════════ */}
        <AccordionItem data-sf-card="true" value="legal" className="sf-card border border-slate-200/80 rounded-xl bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden transition-shadow duration-200 data-[state=open]:border-l-4 data-[state=open]:border-l-blue-500 data-[state=open]:ring-1 data-[state=open]:ring-blue-100 data-[state=open]:shadow-[0_6px_20px_-8px_rgba(37,99,235,0.18)]">
          <AccordionTrigger className="sf-trigger px-5 py-4 hover:no-underline hover:bg-slate-50/60 transition-colors" data-testid="legal-trigger">
            <div className="flex items-center gap-2.5">
              <StepBadge num={5} complete={isLegalComplete} />
              <FileText className="h-4 w-4 text-teal-500 flex-shrink-0" />
              <span className="flex flex-col"><span className="sf-heading sf-sec-title">Legal Description</span><span className="sf-sec-desc">Legal description as recorded</span></span>
              <SectionBadge complete={isLegalComplete} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="sf-content px-5 pb-5">
            <p className="text-xs text-slate-500 mb-3">
              Enter the complete legal description for the property. Please do not reference an attached document.
            </p>

            {/* ── Empty State ── */}
            {!data.legal_description && !legalEditMode && (
              <div className="border border-dashed border-slate-200 rounded-2xl bg-slate-50 py-10 flex flex-col items-center justify-center gap-3" data-testid="legal-empty-state">
                <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
                  <Landmark className="h-6 w-6 text-slate-400" />
                </div>
                <p className="text-sm font-semibold text-slate-600">No legal description added yet</p>
                <p className="text-xs text-slate-400">Add it manually or extract it from an uploaded PDF.</p>
                {!isDisabled && (
                  <div className="flex items-center gap-3 mt-2">
                    <button
                      onClick={() => setLegalEditMode(true)}
                      className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors"
                      data-testid="add-legal-btn"
                    >
                      Add Manually
                    </button>
                    <button
                      onClick={openExtractModal}
                      className="px-4 py-2 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors inline-flex items-center gap-1.5"
                      data-testid="extract-legal-btn"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      Extract from PDF
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── Preview State (data exists, not editing) ── */}
            {data.legal_description && !legalEditMode && (
              <div className="border border-slate-200 rounded-lg bg-slate-50 p-4" data-testid="legal-preview">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <p className="text-slate-700 font-mono text-xs whitespace-pre-wrap leading-relaxed line-clamp-4 flex-1">
                    {data.legal_description}
                  </p>
                  {!isDisabled && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={openExtractModal}
                        className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
                        data-testid="extract-legal-preview-btn"
                      >
                        <Upload className="h-3 w-3" /> Extract from PDF
                      </button>
                      <button
                        onClick={() => setLegalEditMode(true)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                        data-testid="edit-legal-btn"
                      >
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Edit State ── */}
            {legalEditMode && (
              <div data-testid="legal-edit-state">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Legal Description</label>
                  {!isDisabled && (
                    <button
                      onClick={openExtractModal}
                      className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
                      data-testid="extract-legal-edit-btn"
                    >
                      <Upload className="h-3 w-3" /> Extract from PDF
                    </button>
                  )}
                </div>
                <textarea
                  data-testid="legal-description-input"
                  className={`${textareaClass} min-h-[160px] font-mono text-xs`}
                  value={data.legal_description}
                  onChange={e => update('legal_description', e.target.value)}
                  placeholder="Enter the full legal description of the property..."
                  disabled={isDisabled}
                  autoFocus
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={() => setLegalEditMode(false)}
                    className="text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
                    data-testid="done-legal-btn"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* ═══════════ 6. DOT / Mortgage Information ═══════════ */}
        <AccordionItem data-sf-card="true" value="dot-mortgage" className="sf-card border border-slate-200/80 rounded-xl bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden transition-shadow duration-200 data-[state=open]:border-l-4 data-[state=open]:border-l-blue-500 data-[state=open]:ring-1 data-[state=open]:ring-blue-100 data-[state=open]:shadow-[0_6px_20px_-8px_rgba(37,99,235,0.18)]">
          <AccordionTrigger className="sf-trigger px-5 py-4 hover:no-underline hover:bg-slate-50/60 transition-colors" data-testid="dot-mortgage-trigger">
            <div className="flex items-center gap-2.5">
              <StepBadge num={6} complete={isDotMortgageComplete} />
              <Scale className="h-4 w-4 text-purple-500 flex-shrink-0" />
              <span className="flex flex-col"><span className="sf-heading sf-sec-title">DOT / Mortgage Information</span><span className="sf-sec-desc">Open deeds of trust, mortgages and related instruments</span></span>
              <span className="text-[11px] text-slate-400 font-medium">({data.dot_mortgage_information.filter(dotMortgageHasData).length} entries)</span>
              <SectionBadge complete={isDotMortgageComplete} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="sf-content px-5 pb-5">
            <p className="text-xs text-slate-500 mb-4">
              Record each open Deed of Trust / Mortgage found in the search. All entered records print on the search report in order.
            </p>

            {/* ── NO-OPEN-MORTGAGE-001 — affirm no open DOT/Mortgage exists ── */}
            <label
              className={`flex items-start gap-2.5 border rounded-xl px-4 py-3 mb-4 cursor-pointer transition-colors ${data.no_open_mortgage ? 'border-emerald-300 bg-emerald-50/60' : 'border-slate-200 bg-white hover:border-slate-300'}`}
              data-testid="no-open-mortgage-toggle"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-emerald-600"
                checked={data.no_open_mortgage === true}
                onChange={e => handleNoOpenMortgageToggle(e.target.checked)}
                disabled={isDisabled}
                data-testid="no-open-mortgage-checkbox"
              />
              <span>
                <span className="text-[13px] font-semibold text-slate-800">No Open Mortgage</span>
                <span className="block text-[11.5px] text-slate-500 mt-0.5">
                  No open Deed of Trust / Mortgage was found in this search. All mortgage detail sections are hidden and not required — Comments / Notes stays available.
                </span>
              </span>
            </label>

            {/* ── Tabbed DOT/Mortgage Card ── */}
            <div className="border border-slate-200/80 rounded-xl overflow-hidden bg-slate-50/50" data-testid="dot-mortgage-tabbed-card">
              {/* Tab bar + add/remove buttons */}
              <div className="flex items-center bg-slate-100/70 border-b border-slate-200/80">
                <div className="flex items-center flex-1 overflow-x-auto">
                  {data.dot_mortgage_information.map((rec, idx) => (
                    <button
                      key={rec.id}
                      type="button"
                      onClick={() => setActiveDotTab(idx)}
                      className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-r border-slate-200/80 transition-all duration-200 ${
                        activeDotTab === idx
                          ? 'bg-white text-slate-800 shadow-sm border-b-2 border-b-purple-500'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50/80'
                      }`}
                      data-testid={`dot-mortgage-tab-${idx}`}
                    >
                      {rec.type ? `${rec.type} #${idx + 1}` : `#${idx + 1}`}
                    </button>
                  ))}
                </div>
                {!isDisabled && !data.no_open_mortgage && (
                  <div className="flex items-center gap-0.5 px-2 flex-shrink-0">
                    <ResetRecordButton
                      label={`Mortgage/DOT #${activeDotTab + 1}`}
                      disabled={!(() => { const r = data.dot_mortgage_information[activeDotTab] || {}; return mortgageRecordHasClearableData(r) || (r.type || '').trim() || (r.comments || '').trim(); })()}
                      onReset={() => requestReset(`Mortgage/DOT #${activeDotTab + 1}`, () => resetDotMortgage(activeDotTab))}
                      testid="reset-dot-mortgage-btn"
                    />
                    <button
                      type="button"
                      onClick={() => removeDotMortgage(activeDotTab)}
                      className="h-7 w-7 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-300 hover:bg-red-50 transition-colors shadow-sm"
                      data-testid="remove-dot-mortgage-btn"
                      title="Remove current record"
                    >
                      <span className="text-base font-bold leading-none">-</span>
                    </button>
                    <button
                      type="button"
                      onClick={addDotMortgage}
                      className="h-7 w-7 flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
                      data-testid="add-dot-mortgage-tab-btn"
                      title="Add new Mortgage / DOT record"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>

              {/* Active record form */}
              {data.dot_mortgage_information[activeDotTab] && (() => {
                const rec = data.dot_mortgage_information[activeDotTab];
                const idx = activeDotTab;
                // NO-OPEN-MORTGAGE-001 — lock all mortgage-detail fields (Comments stays open)
                const dotLocked = isDisabled || data.no_open_mortgage === true;
                const renderFlatGroup = (group) => (
                  <div key={group.title} className="rounded-xl border border-slate-200/70 bg-slate-50/70 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="h-1.5 w-1.5 rounded-full bg-purple-500 flex-shrink-0"></span>
                      <span className="sf-heading text-[11px] font-extrabold text-slate-600 uppercase tracking-widest">{group.title}</span>
                      <div className="flex-1 border-t border-slate-200/80"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {group.fields
                        .filter(field => !(field.key === 'trustee' && (rec.type || '').trim() === 'Mortgage'))
                        .map(field => {
                        const reqMiss = mandatoryCheckActive && !isExternalPortal && !data.no_open_mortgage && dotMortgageHasData(rec) && flatFieldRequired(field, rec) && !(rec[field.key] || '').trim();
                        return (
                        <div key={field.key} className={field.kind === 'textarea' ? 'md:col-span-3' : ''}>
                          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{field.label}</label>
                          {field.kind === 'textarea' ? (
                            <textarea
                              data-testid={`dot-mortgage-${field.key}-${idx}`}
                              className={`${inputClass} min-h-[64px] resize-y`}
                              rows={2}
                              value={rec[field.key] || ''}
                              onChange={e => updateDotMortgage(idx, field.key, e.target.value)}
                              onBlur={blurFormatter(field) ? (e => updateDotMortgage(idx, field.key, blurFormatter(field)(e.target.value))) : undefined}
                              placeholder={field.placeholder || field.label}
                              disabled={dotLocked}
                            />
                          ) : field.kind === 'select' ? (
                            <div className="relative">
                              <select
                                data-testid={`dot-mortgage-${field.key}-${idx}`}
                                className={`${selectClass}${reqMiss ? REQ_CLASS : ''}`}
                                value={rec[field.key] || ''}
                                onChange={e => updateDotMortgage(idx, field.key, e.target.value)}
                                disabled={dotLocked}
                              >
                                <option value="">Select...</option>
                                {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                              </select>
                              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                              <RequiredError show={reqMiss} />
                            </div>
                          ) : field.kind === 'date' ? (
                            <>
                              <DateField
                                max={noFuture(field) ? getBusinessToday() : undefined}
                                testid={`dot-mortgage-${field.key}-${idx}`}
                                className={`${inputClass}${noFuture(field) ? futureDateClass(rec[field.key]) : ''}${reqMiss ? REQ_CLASS : ''}`}
                                value={rec[field.key] || ''}
                                onCommit={v => updateDotMortgage(idx, field.key, v)}
                                disabled={dotLocked}
                              />
                              {noFuture(field) && <FutureDateError value={rec[field.key]} />}
                              <RequiredError show={reqMiss} />
                            </>
                          ) : field.multiline ? (
                            <>
                              <PartyTextarea
                                data-testid={`dot-mortgage-${field.key}-${idx}`}
                                className={`${inputClass}${reqMiss ? REQ_CLASS : ''}`}
                                value={rec[field.key] || ''}
                                onChange={e => updateDotMortgage(idx, field.key, e.target.value)}
                                onBlur={blurFormatter(field) ? (e => updateDotMortgage(idx, field.key, blurFormatter(field)(e.target.value))) : undefined}
                                placeholder={field.placeholder || field.label}
                                disabled={dotLocked}
                              />
                              <RequiredError show={reqMiss} />
                            </>
                          ) : (
                            <>
                              <input
                                type="text"
                                data-testid={`dot-mortgage-${field.key}-${idx}`}
                                className={`${inputClass}${reqMiss ? REQ_CLASS : ''}`}
                                value={rec[field.key] || ''}
                                onChange={e => updateDotMortgage(idx, field.key, e.target.value)}
                                onBlur={blurFormatter(field) ? (e => updateDotMortgage(idx, field.key, blurFormatter(field)(e.target.value))) : undefined}
                                placeholder={field.placeholder || field.label}
                                disabled={dotLocked}
                              />
                              <RequiredError show={reqMiss} />
                              {/* REQUIRED-AMOUNT-001 — qualifying mortgage selector */}
                              {field.key === 'original_amount' && (
                                <label className={`mt-1.5 flex items-center gap-1.5 text-[11px] font-semibold select-none ${
                                  rec.use_for_required_amount === true ? 'text-emerald-700' : 'text-slate-500'
                                } ${dotLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:text-emerald-700'}`}>
                                  <input
                                    type="checkbox"
                                    checked={rec.use_for_required_amount === true}
                                    onChange={e => setRequiredAmountMortgage(idx, e.target.checked)}
                                    disabled={dotLocked}
                                    className="h-3.5 w-3.5 accent-emerald-600 disabled:cursor-not-allowed"
                                    data-testid={`dot-mortgage-use-required-${idx}`}
                                  />
                                  Use for Required Amount
                                </label>
                              )}
                            </>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                );

                const renderSubSection = (section) => {
                  const secLocked = dotLocked;
                  return (
                  <div key={section.key} className="rounded-xl border border-purple-200/60 bg-purple-50/40 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="h-1.5 w-1.5 rounded-full bg-purple-500 flex-shrink-0"></span>
                      <span className="sf-heading text-[11px] font-extrabold text-purple-700 uppercase tracking-widest">{section.title}</span>
                      <span className="text-[10px] font-bold text-purple-500 bg-white border border-purple-200/60 rounded-full px-2 py-0.5 tabular-nums" data-testid={`${section.key}-count-${idx}`}>
                        {(rec[section.key] || []).length}
                      </span>
                      <div className="flex items-center gap-1 overflow-x-auto flex-1 min-w-0" data-testid={`${section.key}-tabs-${idx}`}>
                        {(rec[section.key] || []).map((sub, i) => (
                          <button
                            key={sub.id}
                            type="button"
                            onClick={() => setSubTab(idx, section.key, i)}
                            className={`px-2.5 py-1 rounded-md text-[11px] font-bold whitespace-nowrap transition-colors ${
                              getSubTab(idx, section, rec) === i
                                ? 'bg-white text-purple-700 border border-purple-300 shadow-sm'
                                : 'text-slate-400 border border-transparent hover:text-purple-600 hover:bg-white/70'
                            }`}
                            data-testid={`${section.key}-tab-${idx}-${i}`}
                          >
                            #{i + 1}
                          </button>
                        ))}
                      </div>
                      {!secLocked && (
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <ResetRecordButton
                            label={`${section.title} #${getSubTab(idx, section, rec) + 1}`}
                            disabled={!subRecordHasData(section, (rec[section.key] || [])[getSubTab(idx, section, rec)] || {})}
                            onReset={() => {
                              const si = getSubTab(idx, section, rec);
                              requestReset(`${section.title} #${si + 1}`, () => resetDotSub(idx, section, si));
                            }}
                            testid={`reset-${section.key}-btn-${idx}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeDotSub(idx, section, getSubTab(idx, section, rec))}
                            disabled={(rec[section.key] || []).length <= 1}
                            className="h-7 w-7 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-300 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-sm"
                            data-testid={`remove-last-${section.key}-btn-${idx}`}
                            title={`Remove selected ${section.title}`}
                          >
                            <span className="text-base font-bold leading-none">-</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => addDotSub(idx, section)}
                            className="h-7 w-7 flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
                            data-testid={`add-${section.key}-btn-${idx}`}
                            title={`Add ${section.title}`}
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      {(() => {
                        const subIdx = getSubTab(idx, section, rec);
                        const sub = (rec[section.key] || [])[subIdx];
                        if (!sub) return null;
                        return (
                        <div key={sub.id} className="border border-slate-200 rounded-lg p-4 bg-white shadow-sm transition-shadow hover:shadow-md" data-testid={`${section.key}-record-${idx}-${subIdx}`}>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{section.title} #{subIdx + 1}</span>
                            {!secLocked && ((rec[section.key] || []).length > 1 || subRecordHasData(section, sub)) && (
                              <button
                                type="button"
                                onClick={() => removeDotSub(idx, section, subIdx)}
                                className="h-7 w-7 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-300 hover:bg-red-50 transition-colors shadow-sm"
                                data-testid={`remove-${section.key}-btn-${idx}-${subIdx}`}
                                title={`Remove ${section.title}`}
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {section.fields.filter(f => !f.group).map(field => (
                              section.key === 'assignments' && field.key === 'assignor' ? (
                                <div key={field.key}>
                                  <label className="flex items-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                                    {field.label}
                                    {sub.assignor_linked && <span className="ml-1.5 text-[9px] font-bold text-blue-500 normal-case tracking-normal">AUTO</span>}
                                    {sub.assignor_overridden && <span className="ml-1.5 text-[9px] font-bold text-amber-600 normal-case tracking-normal">EDITED</span>}
                                    {sub.assignor_overridden && !secLocked && (
                                      <button
                                        type="button"
                                        data-testid={`assignment-assignor-reset-${idx}-${subIdx}`}
                                        onClick={() => resetAssignorAuto(idx, subIdx)}
                                        className="ml-auto text-[10px] font-semibold text-blue-600 hover:text-blue-800 normal-case tracking-normal"
                                      >Reset to AUTO</button>
                                    )}
                                  </label>
                                  <PartyTextarea
                                    data-testid={`${section.key}-${field.key}-${idx}-${subIdx}`}
                                    className={`${inputClass}${sub.assignor_linked ? ' !bg-blue-50/60' : ''}`}
                                    value={sub.assignor || ''}
                                    onChange={e => setAssignorManual(idx, subIdx, e.target.value)}
                                    onBlur={e => { if (sub.assignor_overridden || !sub.assignor_linked) { const v = autoPartyCase(e.target.value); if (v !== e.target.value) setAssignorManual(idx, subIdx, v); } }}
                                    placeholder={subIdx === 0 ? 'Auto-fills from Lender' : `Auto-fills from Assignment #${subIdx} Assignee`}
                                    disabled={secLocked}
                                  />
                                  <p className="text-[10.5px] text-slate-400 mt-1" data-testid={`assignment-assignor-hint-${idx}-${subIdx}`}>
                                    {sub.assignor_overridden
                                      ? 'Manual override — auto-fill paused. Use "Reset to AUTO" to restore the derived value.'
                                      : sub.assignor_linked
                                        ? (subIdx === 0 ? 'Auto-filled from Mortgage/DOT Lender — type to override' : `Auto-filled from Assignment #${subIdx} Assignee — type to override`)
                                        : (subIdx === 0 ? 'Enter manually — Lender is empty' : `Enter manually — Assignment #${subIdx} Assignee is empty`)}
                                  </p>
                                </div>
                              ) : field.kind === 'textarea' ? (
                                <div key={field.key} className="md:col-span-3">
                                  <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{field.label}</label>
                                  <textarea
                                    data-testid={`${section.key}-${field.key}-${idx}-${subIdx}`}
                                    className={textareaClass}
                                    rows={2}
                                    value={sub[field.key] || ''}
                                    onChange={e => updateDotSub(idx, section.key, subIdx, field.key, e.target.value)}
                                    onBlur={blurFormatter(field) ? (e => updateDotSub(idx, section.key, subIdx, field.key, blurFormatter(field)(e.target.value))) : undefined}
                                    placeholder={field.placeholder || field.label}
                                    disabled={secLocked}
                                  />
                                </div>
                              ) : field.kind === 'date' ? (
                                <div key={field.key}>
                                  <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{field.label}</label>
                                  <DateField
                                    max={noFuture(field) ? getBusinessToday() : undefined}
                                    testid={`${section.key}-${field.key}-${idx}-${subIdx}`}
                                    className={`${inputClass}${noFuture(field) ? futureDateClass(sub[field.key]) : ''}${mandatoryCheckActive && !isExternalPortal && !data.no_open_mortgage && subRecordHasData(section, sub) && (MANDATORY_KEYS[section.key] || []).includes(field.key) && !(sub[field.key] || '').trim() ? REQ_CLASS : ''}`}
                                    value={sub[field.key] || ''}
                                    onCommit={v => updateDotSub(idx, section.key, subIdx, field.key, v)}
                                    disabled={secLocked}
                                  />
                                  {noFuture(field) && <FutureDateError value={sub[field.key]} />}
                                  <RequiredError show={mandatoryCheckActive && !isExternalPortal && !data.no_open_mortgage && subRecordHasData(section, sub) && (MANDATORY_KEYS[section.key] || []).includes(field.key) && !(sub[field.key] || '').trim()} />
                                </div>
                              ) : field.multiline ? (
                                <div key={field.key}>
                                  <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{field.label}</label>
                                  <PartyTextarea
                                    data-testid={`${section.key}-${field.key}-${idx}-${subIdx}`}
                                    className={`${inputClass}${mandatoryCheckActive && !isExternalPortal && !data.no_open_mortgage && subRecordHasData(section, sub) && (MANDATORY_KEYS[section.key] || []).includes(field.key) && !(sub[field.key] || '').trim() ? REQ_CLASS : ''}`}
                                    value={sub[field.key] || ''}
                                    onChange={e => updateDotSub(idx, section.key, subIdx, field.key, e.target.value)}
                                    onBlur={blurFormatter(field) ? (e => updateDotSub(idx, section.key, subIdx, field.key, blurFormatter(field)(e.target.value))) : undefined}
                                    placeholder={field.placeholder || field.label}
                                    disabled={secLocked}
                                  />
                                  <RequiredError show={mandatoryCheckActive && !isExternalPortal && !data.no_open_mortgage && subRecordHasData(section, sub) && (MANDATORY_KEYS[section.key] || []).includes(field.key) && !(sub[field.key] || '').trim()} />
                                </div>
                              ) : (
                                <div key={field.key}>
                                  <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{field.label}</label>
                                  <input
                                    type="text"
                                    data-testid={`${section.key}-${field.key}-${idx}-${subIdx}`}
                                    className={`${inputClass}${mandatoryCheckActive && !isExternalPortal && !data.no_open_mortgage && subRecordHasData(section, sub) && (MANDATORY_KEYS[section.key] || []).includes(field.key) && !(sub[field.key] || '').trim() ? REQ_CLASS : ''}`}
                                    value={sub[field.key] || ''}
                                    onChange={e => updateDotSub(idx, section.key, subIdx, field.key, e.target.value)}
                                    onBlur={blurFormatter(field) ? (e => updateDotSub(idx, section.key, subIdx, field.key, blurFormatter(field)(e.target.value))) : undefined}
                                    placeholder={field.placeholder || field.label}
                                    disabled={secLocked}
                                  />
                                  <RequiredError show={mandatoryCheckActive && !isExternalPortal && !data.no_open_mortgage && subRecordHasData(section, sub) && (MANDATORY_KEYS[section.key] || []).includes(field.key) && !(sub[field.key] || '').trim()} />
                                </div>
                              )
                            ))}
                          </div>
                          {/* Grouped fields (e.g. Recording Information) — divider + own grid */}
                          {[...new Set(section.fields.filter(f => f.group).map(f => f.group))].map(groupName => (
                            <div key={groupName} className="mt-4">
                              <div className="flex items-center gap-2 mb-3">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap">{groupName}</span>
                                <div className="flex-1 border-t border-dashed border-slate-200"></div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {section.fields.filter(f => f.group === groupName).map(field => (
                                  field.kind === 'textarea' ? (
                                    <div key={field.key} className="md:col-span-3">
                                      <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{field.label}</label>
                                      <textarea
                                        data-testid={`${section.key}-${field.key}-${idx}-${subIdx}`}
                                        className={textareaClass}
                                        rows={2}
                                        value={sub[field.key] || ''}
                                        onChange={e => updateDotSub(idx, section.key, subIdx, field.key, e.target.value)}
                                        onBlur={blurFormatter(field) ? (e => updateDotSub(idx, section.key, subIdx, field.key, blurFormatter(field)(e.target.value))) : undefined}
                                        placeholder={field.placeholder || field.label}
                                        disabled={secLocked}
                                      />
                                    </div>
                                  ) : field.kind === 'date' ? (
                                    <div key={field.key}>
                                      <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{field.label}</label>
                                      <DateField
                                        max={noFuture(field) ? getBusinessToday() : undefined}
                                        testid={`${section.key}-${field.key}-${idx}-${subIdx}`}
                                        className={`${inputClass}${noFuture(field) ? futureDateClass(sub[field.key]) : ''}${mandatoryCheckActive && !isExternalPortal && !data.no_open_mortgage && subRecordHasData(section, sub) && (MANDATORY_KEYS[section.key] || []).includes(field.key) && !(sub[field.key] || '').trim() ? REQ_CLASS : ''}`}
                                        value={sub[field.key] || ''}
                                        onCommit={v => updateDotSub(idx, section.key, subIdx, field.key, v)}
                                        disabled={secLocked}
                                      />
                                      {noFuture(field) && <FutureDateError value={sub[field.key]} />}
                                      <RequiredError show={mandatoryCheckActive && !isExternalPortal && !data.no_open_mortgage && subRecordHasData(section, sub) && (MANDATORY_KEYS[section.key] || []).includes(field.key) && !(sub[field.key] || '').trim()} />
                                    </div>
                                  ) : (
                                    <div key={field.key}>
                                      <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{field.label}</label>
                                      <input
                                        type="text"
                                        data-testid={`${section.key}-${field.key}-${idx}-${subIdx}`}
                                        className={`${inputClass}${mandatoryCheckActive && !isExternalPortal && !data.no_open_mortgage && subRecordHasData(section, sub) && (MANDATORY_KEYS[section.key] || []).includes(field.key) && !(sub[field.key] || '').trim() ? REQ_CLASS : ''}`}
                                        value={sub[field.key] || ''}
                                        onChange={e => updateDotSub(idx, section.key, subIdx, field.key, e.target.value)}
                                        onBlur={blurFormatter(field) ? (e => updateDotSub(idx, section.key, subIdx, field.key, blurFormatter(field)(e.target.value))) : undefined}
                                        placeholder={field.placeholder || field.label}
                                        disabled={secLocked}
                                      />
                                      <RequiredError show={mandatoryCheckActive && !isExternalPortal && !data.no_open_mortgage && subRecordHasData(section, sub) && (MANDATORY_KEYS[section.key] || []).includes(field.key) && !(sub[field.key] || '').trim()} />
                                    </div>
                                  )
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        );
                      })()}
                    </div>
                  </div>
                  );
                };

                return (
                  <div className="p-5 bg-white space-y-5" data-testid={`dot-mortgage-form-${idx}`}>
                    {/* NO-OPEN-MORTGAGE-002 — ALL mortgage detail sections
                        (incl. Notice of Pendency) collapse entirely when the
                        flag is set; only the banner and Comments / Notes stay. */}
                    {!data.no_open_mortgage && renderFlatGroup(DOT_FLAT_GROUPS[0])} {/* Instrument */}
                    {!data.no_open_mortgage && renderFlatGroup(DOT_FLAT_GROUPS[1])} {/* Parties */}
                    {/* Comments / Notes — re-recorded mortgage details */}
                    <div className="rounded-xl border border-amber-200/60 bg-amber-50/40 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0"></span>
                        <span className="sf-heading text-[11px] font-extrabold text-amber-700 uppercase tracking-widest">Comments / Notes</span>
                        <div className="flex-1 border-t border-amber-200/50"></div>
                      </div>
                      <textarea
                        data-testid={`dot-mortgage-comments-${idx}`}
                        className={textareaClass}
                        rows={3}
                        value={rec.comments || ''}
                        onChange={e => updateDotMortgage(idx, 'comments', e.target.value)}
                        onBlur={e => updateDotMortgage(idx, 'comments', autoCommentsCase(e.target.value))}
                        placeholder="Details relating to any re-recorded mortgage…"
                        disabled={isDisabled}
                      />
                    </div>
                    {!data.no_open_mortgage && renderSubSection(DOT_SUB_SECTIONS[0])} {/* Assignment */}
                    {!data.no_open_mortgage && renderSubSection(DOT_SUB_SECTIONS[1])} {/* Modification */}
                    {!data.no_open_mortgage && renderSubSection(DOT_SUB_SECTIONS[2])} {/* Appointed Trustee */}
                    {!data.no_open_mortgage && renderSubSection(DOT_SUB_SECTIONS[3])} {/* Subordination Agreement */}
                    {!data.no_open_mortgage && renderSubSection(DOT_SUB_SECTIONS[4])} {/* Notice of Pendency */}
                  </div>
                );
              })()}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ═══════════ 7. Judgment / Lien Types ═══════════ */}
        <AccordionItem data-sf-card="true" value="judgment-lien" className="sf-card border border-slate-200/80 rounded-xl bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden transition-shadow duration-200 data-[state=open]:border-l-4 data-[state=open]:border-l-blue-500 data-[state=open]:ring-1 data-[state=open]:ring-blue-100 data-[state=open]:shadow-[0_6px_20px_-8px_rgba(37,99,235,0.18)]">
          <AccordionTrigger className="sf-trigger px-5 py-4 hover:no-underline hover:bg-slate-50/60 transition-colors" data-testid="judgment-lien-trigger">
            <div className="flex items-center gap-2.5">
              <StepBadge num={7} complete={isJudgmentLienComplete} />
              <Landmark className="h-4 w-4 text-indigo-500 flex-shrink-0" />
              <span className="flex flex-col"><span className="sf-heading sf-sec-title">Judgment / Lien Types</span><span className="sf-sec-desc">Judgments, liens and legal actions affecting title</span></span>
              <span className="text-[11px] text-slate-400 font-medium">({data.legal_actions.filter(legalActionHasData).length} entries)</span>
              <SectionBadge complete={isJudgmentLienComplete} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="sf-content px-5 pb-5">
            <div className="flex items-center justify-between gap-3 mb-4">
              <p className="text-xs text-slate-500">
                Record each judgment, lien, or legal action found in the search. Each entry is independent — add as many as needed.
              </p>
              {!isDisabled && (
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <ResetRecordButton
                    label={`Judgment/Lien #${Math.min(activeLaTab, data.legal_actions.length - 1) + 1}`}
                    disabled={!legalActionHasData(data.legal_actions[Math.min(activeLaTab, data.legal_actions.length - 1)] || {})}
                    onReset={() => {
                      const li = Math.min(activeLaTab, data.legal_actions.length - 1);
                      requestReset(`Judgment/Lien #${li + 1}`, () => resetLegalAction(li));
                    }}
                    testid="reset-judgment-lien-btn"
                  />
                  <button
                    type="button"
                    onClick={() => removeLegalAction(Math.min(activeLaTab, data.legal_actions.length - 1))}
                    disabled={data.legal_actions.length <= 1}
                    className="h-7 w-7 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-300 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-sm"
                    data-testid="remove-last-judgment-lien-btn"
                    title="Remove selected Judgment / Lien Type"
                  >
                    <span className="text-base font-bold leading-none">-</span>
                  </button>
                  <button
                    type="button"
                    onClick={addLegalAction}
                    className="h-7 w-7 flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
                    data-testid="add-judgment-lien-btn"
                    title="Add Judgment / Lien Type"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 overflow-x-auto mb-3" data-testid="judgment-lien-tabs">
              {data.legal_actions.map((r, i) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setActiveLaTab(i)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-bold whitespace-nowrap transition-colors ${
                    Math.min(activeLaTab, data.legal_actions.length - 1) === i
                      ? 'bg-white text-indigo-700 border border-indigo-300 shadow-sm'
                      : 'text-slate-400 border border-transparent hover:text-indigo-600 hover:bg-slate-50'
                  }`}
                  data-testid={`judgment-lien-tab-${i}`}
                >
                  #{i + 1}
                </button>
              ))}
            </div>
            <div className="space-y-3">
              {(() => {
                const idx = Math.min(activeLaTab, data.legal_actions.length - 1);
                const rec = data.legal_actions[idx];
                if (!rec) return null;
                return (
                <div key={rec.id} className="border border-slate-200 rounded-lg p-4 bg-white shadow-sm transition-shadow hover:shadow-md" data-testid={`judgment-lien-record-${idx}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Judgment / Lien Type #{idx + 1}</span>
                    {!isDisabled && (data.legal_actions.length > 1 || legalActionHasData(rec)) && (
                      <button
                        type="button"
                        onClick={() => removeLegalAction(idx)}
                        className="h-7 w-7 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-300 hover:bg-red-50 transition-colors shadow-sm"
                        data-testid={`remove-judgment-lien-btn-${idx}`}
                        title="Remove Judgment / Lien Type"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {LEGAL_ACTION_FIELDS.map(field => (
                      field.kind === 'textarea' ? (
                        <div key={field.key} className="md:col-span-3">
                          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{field.label}</label>
                          <textarea
                            data-testid={`judgment-lien-${field.key}-${idx}`}
                            className={textareaClass}
                            rows={2}
                            value={rec[field.key] || ''}
                            onChange={e => updateLegalAction(idx, field.key, e.target.value)}
                            onBlur={blurFormatter(field) ? (e => updateLegalAction(idx, field.key, blurFormatter(field)(e.target.value))) : undefined}
                            placeholder={field.placeholder || field.label}
                            disabled={isDisabled}
                          />
                        </div>
                      ) : field.kind === 'date' ? (
                        <div key={field.key}>
                          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{field.label}</label>
                          <DateField
                            max={getBusinessToday()}
                            testid={`judgment-lien-${field.key}-${idx}`}
                            className={`${inputClass}${futureDateClass(rec[field.key])}${mandatoryCheckActive && !isExternalPortal && legalActionHasData(rec) && MANDATORY_KEYS.legal_action.includes(field.key) && !(rec[field.key] || '').trim() ? REQ_CLASS : ''}`}
                            value={rec[field.key] || ''}
                            onCommit={v => updateLegalAction(idx, field.key, v)}
                            disabled={isDisabled}
                          />
                          <FutureDateError value={rec[field.key]} />
                          <RequiredError show={mandatoryCheckActive && !isExternalPortal && legalActionHasData(rec) && MANDATORY_KEYS.legal_action.includes(field.key) && !(rec[field.key] || '').trim()} />
                        </div>
                      ) : field.multiline ? (
                        <div key={field.key}>
                          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{field.label}</label>
                          <PartyTextarea
                            data-testid={`judgment-lien-${field.key}-${idx}`}
                            className={`${inputClass}${mandatoryCheckActive && !isExternalPortal && legalActionHasData(rec) && MANDATORY_KEYS.legal_action.includes(field.key) && !(rec[field.key] || '').trim() ? REQ_CLASS : ''}`}
                            value={rec[field.key] || ''}
                            onChange={e => updateLegalAction(idx, field.key, e.target.value)}
                            onBlur={blurFormatter(field) ? (e => updateLegalAction(idx, field.key, blurFormatter(field)(e.target.value))) : undefined}
                            placeholder={field.placeholder || field.label}
                            disabled={isDisabled}
                          />
                          <RequiredError show={mandatoryCheckActive && !isExternalPortal && legalActionHasData(rec) && MANDATORY_KEYS.legal_action.includes(field.key) && !(rec[field.key] || '').trim()} />
                        </div>
                      ) : (
                        <div key={field.key}>
                          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{field.label}</label>
                          <input
                            type="text"
                            data-testid={`judgment-lien-${field.key}-${idx}`}
                            className={`${inputClass}${mandatoryCheckActive && !isExternalPortal && legalActionHasData(rec) && MANDATORY_KEYS.legal_action.includes(field.key) && !(rec[field.key] || '').trim() ? REQ_CLASS : ''}`}
                            value={rec[field.key] || ''}
                            onChange={e => updateLegalAction(idx, field.key, e.target.value)}
                            onBlur={blurFormatter(field) ? (e => updateLegalAction(idx, field.key, blurFormatter(field)(e.target.value))) : undefined}
                            placeholder={field.placeholder || field.label}
                            disabled={isDisabled}
                          />
                          <RequiredError show={mandatoryCheckActive && !isExternalPortal && legalActionHasData(rec) && MANDATORY_KEYS.legal_action.includes(field.key) && !(rec[field.key] || '').trim()} />
                        </div>
                      )
                    ))}
                  </div>
                </div>
                );
              })()}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ═══════════ 8. Names Run (NAMES-RUN-001) ═══════════ */}
        <AccordionItem data-sf-card="true" value="names-run" className="sf-card border border-slate-200/80 rounded-xl bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden transition-shadow duration-200 data-[state=open]:border-l-4 data-[state=open]:border-l-blue-500 data-[state=open]:ring-1 data-[state=open]:ring-blue-100 data-[state=open]:shadow-[0_6px_20px_-8px_rgba(37,99,235,0.18)]">
          <AccordionTrigger className="sf-trigger px-5 py-4 hover:no-underline hover:bg-slate-50/60 transition-colors" data-testid="names-run-trigger">
            <div className="flex items-center gap-2.5">
              <StepBadge num={8} complete={data.names_run.some(n => (n.value || '').trim())} />
              <Search className="h-4 w-4 text-cyan-600 flex-shrink-0" />
              <span className="flex flex-col"><span className="sf-heading sf-sec-title">Names Run</span><span className="sf-sec-desc">Names searched for this order</span></span>
              <span className="text-[11px] text-slate-400 font-medium">({data.names_run.filter(n => (n.value || '').trim()).length} name{data.names_run.filter(n => (n.value || '').trim()).length === 1 ? '' : 's'})</span>
              <SectionBadge complete={data.names_run.some(n => (n.value || '').trim())} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="sf-content px-5 pb-5">
            <p className="text-xs text-slate-500 mb-3">
              Record each individual/entity name that was searched during the title search. These flow into the final report's "Names Run" section.
            </p>
            <div className="space-y-2 max-w-lg">
              {data.names_run.map((n, idx) => (
                <div key={n.id} className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-16 flex-shrink-0">Name #{idx + 1}</label>
                  <input
                    type="text"
                    data-testid={`names-run-input-${idx}`}
                    className={inputClass}
                    value={n.value}
                    onChange={e => updateNameRun(idx, e.target.value)}
                    onBlur={e => updateNameRun(idx, autoPartyCase(e.target.value))}
                    placeholder="e.g. Michael J. Ahrens / ABC Holdings LLC"
                    disabled={isDisabled}
                  />
                  {!isDisabled && (data.names_run.length > 1 || (n.value || '').trim()) && (
                    <button
                      type="button"
                      onClick={() => removeNameRun(idx)}
                      className="h-7 w-7 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-300 hover:bg-red-50 transition-colors shadow-sm flex-shrink-0"
                      data-testid={`remove-names-run-btn-${idx}`}
                      title="Remove name"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {!isDisabled && (
                <button
                  type="button"
                  onClick={addNameRun}
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-blue-600 hover:text-blue-800 mt-1"
                  data-testid="add-names-run-btn"
                >
                  <Plus className="h-3.5 w-3.5" /> Add Name
                </button>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ═══════════ 9. Documents ═══════════ */}
        <AccordionItem data-sf-card="true" value="documents" className="sf-card border border-slate-200/80 rounded-xl bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden transition-shadow duration-200 data-[state=open]:border-l-4 data-[state=open]:border-l-blue-500 data-[state=open]:ring-1 data-[state=open]:ring-blue-100 data-[state=open]:shadow-[0_6px_20px_-8px_rgba(37,99,235,0.18)]">
          <AccordionTrigger className="sf-trigger px-5 py-4 hover:no-underline hover:bg-slate-50/60 transition-colors" data-testid="documents-trigger">
            <div className="flex items-center gap-2.5">
              <StepBadge num={9} complete={isDocsComplete} required={primaryDocMissing} />
              <FileCheck className="h-4 w-4 text-rose-500 flex-shrink-0" />
              <span className="flex flex-col"><span className="sf-heading sf-sec-title">Documents</span><span className="sf-sec-desc">Primary search document and supporting files</span></span>
              <SectionBadge complete={isDocsComplete} required={primaryDocMissing} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="sf-content px-5 pb-5">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Primary Search Document <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-slate-400 mb-2">Select the uploaded file that represents the final search product</p>
                {files.length > 0 && primaryDocMissing && (
                  <div className="mb-2 flex items-start gap-2 rounded-lg border border-amber-300/70 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="primary-document-select-required">
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span><strong>Required:</strong> {files.length} file{files.length === 1 ? '' : 's'} uploaded — uploading alone is not enough. Select the completed search document below as the Primary Search Document to unlock Complete / QC Review.</span>
                  </div>
                )}
                {files.length > 0 ? (
                  <div className="relative max-w-md">
                    <select
                      data-testid="primary-document-select"
                      className={selectClass}
                      value={data.primary_document_id || ''}
                      onChange={e => {
                        const fileId = e.target.value;
                        const file = files.find(f => f.id === fileId);
                        update('primary_document_id', fileId || null);
                        update('primary_document_name', file?.filename || null);
                      }}
                      disabled={isDisabled}
                    >
                      <option value="">-- Select a file --</option>
                      {files.map(f => (
                        <option key={f.id} value={f.id}>{f.filename}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  </div>
                ) : (
                  primaryDocMissing ? (
                    <div className="border border-dashed border-amber-300 bg-amber-50/60 rounded-lg p-4 text-center" data-testid="primary-document-required-callout">
                      <p className="text-sm font-semibold text-amber-800 flex items-center justify-center gap-1.5"><AlertCircle className="h-4 w-4" />Primary Search Document required</p>
                      <p className="text-xs text-amber-700 mt-1">Upload the completed search document in the <strong>Files</strong> tab, then come back and select it here. Uploading alone is not enough — the file must be selected as the Primary Search Document before Complete or QC Review is available.</p>
                    </div>
                  ) : (
                    <div className="border border-dashed border-slate-300 rounded-lg p-4 text-center">
                      <p className="text-sm text-slate-500">No files uploaded yet. Upload files via the <strong>Files</strong> tab.</p>
                    </div>
                  )
                )}
              </div>
              {data.primary_document_id && (
                <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>Primary document: <strong>{data.primary_document_name || 'Selected'}</strong></span>
                </div>
              )}
              {files.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Supporting Documents</label>
                  <p className="text-xs text-slate-400">{files.filter(f => f.id !== data.primary_document_id).length} additional file(s) available in the Files tab</p>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ═══════════ 9. Finalize / Complete ═══════════ */}
        <AccordionItem data-sf-card="true" value="finalize" className="sf-card border border-slate-200/80 rounded-xl bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden transition-shadow duration-200 data-[state=open]:border-l-4 data-[state=open]:border-l-blue-500 data-[state=open]:ring-1 data-[state=open]:ring-blue-100 data-[state=open]:shadow-[0_6px_20px_-8px_rgba(37,99,235,0.18)]">
          <AccordionTrigger className="sf-trigger px-5 py-4 hover:no-underline hover:bg-slate-50/60 transition-colors" data-testid="finalize-trigger">
            <div className="flex items-center gap-2.5">
              <StepBadge num={10} complete={completionPercent === 100} />
              <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
              <span className="flex flex-col"><span className="sf-heading sf-sec-title">Finalize / Complete</span><span className="sf-sec-desc">Final review, QC hand-off and completion</span></span>
              {completionPercent === 100 && (
                <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Ready</span>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="sf-content px-5 pb-5">
            {/* Validation errors from server — shown for ALL orders, incl. external
                portal (ORDER-COMPLETION-VALIDATION-001 supporting-document gate). */}
            {showValidation && validationErrors.length > 0 && (
              <div className="mb-4 border border-red-200 bg-red-50 rounded-lg p-3" data-testid="validation-errors">
                <h4 className="text-xs font-semibold text-red-700 mb-1">Validation Errors</h4>
                <ul className="list-disc list-inside text-xs text-red-600 space-y-0.5">
                  {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}
            <p className="text-[12px] text-slate-500 italic">Use the action bar at the bottom of the screen to complete, submit for QC review, or change the order status.</p>
          </AccordionContent>
        </AccordionItem>

      </Accordion>
        </div>

        {/* ─── Right: Summary Sidebar ─── */}
        <div className="w-72 xl:w-[25%] xl:max-w-[420px] flex-shrink-0 space-y-3.5 sticky top-20 self-start" data-testid="search-sidebar">

          {/* Search Instructions Card — INSTR-PANEL-001 */}
          <SearchInstructionsCard
            text={orderData?.search_instructions || orderData?.notes}
            priorityNotes={orderData?.priority_notes}
          />

          {/* ── Research Links (RESEARCH-LINKS-001) — auto-resolved from
              property State/County/City; manual confirm only on failure ── */}
          <ResearchLinksCard orderId={orderId} readOnly={readOnly || typistMode} />

          {/* ── Live Progress Dashboard ── */}
          <div className="rounded-xl border border-slate-200/80 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden" data-testid="progress-dashboard-card">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
              <h3 className="sf-heading text-[11px] font-extrabold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <ShieldCheck className="h-3 w-3" /> Progress
              </h3>
            </div>
            <div className="px-4 py-4">
              {/* Ring + stats */}
              <div className="flex items-center gap-4">
                <ProgressRing percent={completionPercent} />
                <div className="min-w-0">
                  <p className="sf-heading text-[13px] font-extrabold text-slate-800 leading-tight">
                    {completionPercent === 100 ? 'Ready to complete' : 'Overall Progress'}
                  </p>
                  <p className="text-[11.5px] text-slate-500 mt-0.5 tabular-nums" data-testid="progress-sections-count">
                    {completedCount} of {completionChecks.length} required sections
                  </p>
                  <div className="mt-1.5 inline-flex items-center gap-1.5 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5 max-w-full">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse flex-shrink-0"></span>
                    <span className="text-[10.5px] font-semibold text-blue-700 truncate" data-testid="current-section-indicator">{currentSectionLabel}</span>
                  </div>
                </div>
              </div>
              {/* Guided checklist — click to jump */}
              <div className="mt-4 pt-3 border-t border-slate-100 space-y-1" data-testid="checklist-card">
                {SECTION_META.map(item => {
                  const ok = sectionOkMap[item.id];
                  const active = currentSectionId === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => goToSection(item.id)}
                      className={`w-full flex items-center gap-2.5 text-[12px] rounded-lg px-2 py-1.5 text-left transition-colors ${
                        active ? 'bg-blue-50/80 ring-1 ring-blue-100' : 'hover:bg-slate-50'
                      }`}
                      data-testid={`checklist-item-${item.id}`}
                    >
                      {ok
                        ? <span className="h-[18px] w-[18px] rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0"><Check className="h-3 w-3 text-white" strokeWidth={3} /></span>
                        : <span className={`h-[18px] w-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 text-[9px] font-extrabold tabular-nums ${item.id === 'documents' && primaryDocMissing ? 'border-amber-400 text-amber-700' : active ? 'border-blue-400 text-blue-600' : 'border-slate-200 text-slate-400'}`}>{item.num}</span>
                      }
                      <span className={`truncate ${ok ? 'text-slate-600' : active ? 'text-blue-700 font-semibold' : 'text-slate-400'}`}>
                        {item.label}
                        {item.optional && <span className="text-slate-300 ml-1">(optional)</span>}
                        {item.id === 'documents' && primaryDocMissing && <span className="text-amber-600 font-semibold ml-1" data-testid="checklist-documents-required">Required</span>}
                      </span>
                      {active && <span className="ml-auto text-[9px] font-bold text-blue-500 uppercase tracking-wider flex-shrink-0">Here</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Order Snapshot — restyled key-value grid ── */}
          <div className="rounded-xl border border-slate-200/80 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden" data-testid="order-snapshot-card">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
              <h3 className="sf-heading text-[11px] font-extrabold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Search className="h-3 w-3" /> Order Snapshot
              </h3>
            </div>
            <div className="px-4 py-3 space-y-2 text-[12px]">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[11.5px] font-semibold text-slate-700 truncate">{orderData?.order_number || '—'}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded transition-colors duration-200 flex-shrink-0 ${
                  localStatus === 'completed' ? 'text-emerald-700 bg-emerald-50' :
                  localStatus === 'open' ? 'text-blue-700 bg-blue-50' :
                  localStatus === 'clarification' ? 'text-orange-700 bg-orange-50' :
                  localStatus === 'qc_review' ? 'text-blue-700 bg-blue-50' :
                  'text-amber-700 bg-amber-50'
                }`} data-testid="inline-order-status-chip">{localStatus?.replace('_', ' ') || '—'}</span>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                <div className="text-slate-700 font-semibold leading-snug truncate" title={orderData?.property_address}>{orderData?.property_address || '—'}</div>
                <div className="text-slate-400 text-[11.5px] truncate mt-0.5">{[orderData?.property_city, orderData?.property_state].filter(Boolean).join(', ')}{orderData?.property_county ? ` · ${orderData.property_county} Co.` : ''}</div>
              </div>
              {/* SELLER-NAME-001: current owner being searched — only when captured */}
              {orderData?.seller_name && (
                <div className="rounded-lg bg-amber-50/70 border border-amber-100 px-3 py-2" data-testid="snapshot-seller">
                  <div className="text-[9.5px] font-bold text-amber-700 uppercase tracking-wider">Seller (Current Owner)</div>
                  <div className="text-slate-700 font-semibold leading-snug truncate" title={orderData.seller_name} data-testid="snapshot-seller-name">{orderData.seller_name}</div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 pt-1">
                <div className="min-w-0">
                  <div className="text-[9.5px] font-bold text-slate-400 uppercase tracking-wider">Product</div>
                  {/* Client Product — same "{client_product} [CODE]" chip pattern as the
                      Orders grid (ProductWithCode): name may truncate, chip never does;
                      tooltip carries the full "{product} - {code}" text. */}
                  <div className="text-slate-600 font-medium min-w-0" data-testid="snapshot-client-product">
                    <ProductWithCode
                      product={orderData?.client_product || '—'}
                      code={orderData?.contract_code}
                      codeTestId="snapshot-contract-code"
                      className="w-full"
                      size="xs"
                    />
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-[9.5px] font-bold text-slate-400 uppercase tracking-wider">Client</div>
                  <div className="text-slate-600 font-medium truncate">{orderData?.client_code || '—'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* ═══════════ Typing Actions (TYPING-DELIVER-001) ═══════════
          Same shared component + Phase 2 endpoints as the Order Summary
          modal — one implementation, two entry points. State-driven. */}
      {typistMode && (orderData?.status === 'typing' || orderData?.typing_status === 'completed') && (
        <div data-testid="fulfillment-typing-actions" className="pb-20">
          <TypingOrderSection
            orderData={orderData}
            currentUser={currentUser}
            onUpdate={onOrderUpdate}
            bottomBar
          />
        </div>
      )}

      {/* ═══════════ Sticky Action Bar ═══════════ */}
      {/* TYPING-EDIT-001 — Search workflow actions (Pending/Clarification/QC/Complete)
          are never shown to typists; Typing completion runs from the Typing workspace. */}
      {!isCompleted && !readOnly && !typistMode && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-t border-slate-200 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]" data-testid="search-action-bar">
          <div className="od-wide py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <div className="flex items-center gap-1.5">
                <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${completionPercent}%`,
                      background: completionPercent === 100 ? '#10b981' : '#3b82f6',
                    }}
                  />
                </div>
                <span className="text-[11px] font-medium tabular-nums">{completionPercent}%</span>
              </div>
              <span className="text-slate-300">|</span>
              <span className="text-[12px]">{completedCount}/{completionChecks.length} sections</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className={`text-[12px] h-8 transition-colors duration-150 ${
                  activeAction === 'pending' || localStatus === 'pending'
                    ? 'bg-amber-500 hover:bg-amber-600 border-amber-500 text-white shadow-sm ring-2 ring-amber-300/60'
                    : 'border-amber-200 text-amber-700 hover:bg-amber-50'
                }`}
                onClick={() => openStatusModal('pending')}
                disabled={inflightAction === 'complete'}
                aria-pressed={activeAction === 'pending' || localStatus === 'pending'}
                data-testid="action-pending"
              >
                {inflightAction === 'pending'
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : <Clock className="h-3.5 w-3.5 mr-1" />}
                Pending
              </Button>
              <Button
                variant="outline"
                size="sm"
                className={`text-[12px] h-8 transition-colors duration-150 ${
                  activeAction === 'clarification' || localStatus === 'clarification'
                    ? 'bg-orange-500 hover:bg-orange-600 border-orange-500 text-white shadow-sm ring-2 ring-orange-300/60'
                    : 'border-orange-200 text-orange-700 hover:bg-orange-50'
                }`}
                onClick={() => openStatusModal('clarification')}
                disabled={inflightAction === 'complete'}
                aria-pressed={activeAction === 'clarification' || localStatus === 'clarification'}
                data-testid="action-clarification"
              >
                {inflightAction === 'clarification'
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : <AlertTriangle className="h-3.5 w-3.5 mr-1" />}
                Clarification
              </Button>
              {/* PRIMARY-DOC-UX-002 — disabled with reason while the Primary Search Document is missing;
                  clicking the disabled control guides the processor to the Documents section. */}
              <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex"
                    tabIndex={primaryDocMissing ? 0 : -1}
                    onClick={primaryDocMissing ? guideToDocuments : undefined}
                    data-testid="action-qc-review-wrapper"
                  >
              <Button
                variant="outline"
                size="sm"
                className={`text-[12px] h-8 transition-colors duration-150 ${
                  activeAction === 'qc_review' || localStatus === 'qc_review'
                    ? 'bg-blue-500 hover:bg-blue-600 border-blue-500 text-white shadow-sm ring-2 ring-blue-300/60'
                    : 'border-blue-200 text-blue-700 hover:bg-blue-50'
                } ${primaryDocMissing ? 'pointer-events-none' : ''}`}
                onClick={() => (isExternalPortal ? handleQCReview() : openFinalReview('qc_review'))}
                disabled={inflightAction !== null || primaryDocMissing}
                aria-pressed={activeAction === 'qc_review' || localStatus === 'qc_review'}
                aria-describedby={primaryDocMissing ? 'primary-doc-block-qc' : undefined}
                data-testid="action-qc-review"
              >
                {inflightAction === 'qc_review'
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : primaryDocMissing ? <Info className="h-3.5 w-3.5 mr-1" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
                QC Review
              </Button>
                  </span>
                </TooltipTrigger>
                {primaryDocMissing && (
                  <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed" id="primary-doc-block-qc" data-testid="action-qc-review-tooltip">
                    {PRIMARY_DOC_BLOCK_QC}
                  </TooltipContent>
                )}
              </Tooltip>
              </TooltipProvider>
              {/* QC-UX-001 — Complete is hidden when this user's submissions
                  must go through QC Review (requires_qc processors).
                  TYPING-LOCK-MSG-001 — while the order sits in the Typing queue,
                  completion belongs to the Typing Processor (POST /typing/complete);
                  the Search "Complete" button is replaced by an explicit note. */}
              {localStatus === 'typing' ? (
                <span
                  className="inline-flex items-center h-8 px-2.5 rounded-md border border-cyan-200 bg-cyan-50 text-cyan-800 text-[12px]"
                  data-testid="action-complete-typing-note"
                >
                  <Info className="h-3.5 w-3.5 mr-1" />
                  In Typing queue — completed from the Typing dashboard
                </span>
              ) : !qcReviewRequired && (
              <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex"
                    tabIndex={primaryDocMissing ? 0 : -1}
                    onClick={primaryDocMissing ? guideToDocuments : undefined}
                    data-testid="action-complete-wrapper"
                  >
              <Button
                size="sm"
                className={`text-[12px] h-8 text-white transition-colors duration-150 ${
                  activeAction === 'complete' || localStatus === 'completed'
                    ? 'bg-emerald-700 hover:bg-emerald-800 ring-2 ring-emerald-300/60 shadow-sm'
                    : 'bg-emerald-600 hover:bg-emerald-700'
                } ${primaryDocMissing ? 'pointer-events-none' : ''}`}
                onClick={() => (isExternalPortal ? handleComplete() : openFinalReview('complete'))}
                disabled={completing || inflightAction !== null || primaryDocMissing}
                aria-pressed={activeAction === 'complete' || localStatus === 'completed'}
                aria-describedby={primaryDocMissing ? 'primary-doc-block-complete' : undefined}
                data-testid="action-complete"
              >
                {inflightAction === 'complete' || completing
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : primaryDocMissing ? <Info className="h-3.5 w-3.5 mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                Complete
              </Button>
                  </span>
                </TooltipTrigger>
                {primaryDocMissing && (
                  <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed" id="primary-doc-block-complete" data-testid="action-complete-tooltip">
                    {PRIMARY_DOC_BLOCK_COMPLETE}
                  </TooltipContent>
                )}
              </Tooltip>
              </TooltipProvider>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ Status Change Modal (Pending / Clarification) ═══════════ */}
      {/* RESET-RECORD-001 — per-record reset confirmation */}
      <Dialog open={!!resetConfirm} onOpenChange={(open) => { if (!open) setResetConfirm(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="record-reset-confirm-modal">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-amber-500" />
              Reset {resetConfirm?.label}?
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-1 text-left">
              This will permanently clear all information entered for{' '}
              <span className="font-semibold">{resetConfirm?.label}</span>. Other records and
              sections are not affected. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setResetConfirm(null)} data-testid="record-reset-cancel-btn">
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => { resetConfirm.action(); setResetConfirm(null); }}
              data-testid="record-reset-confirm-btn"
            >
              Reset Record
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* TYPE-RESET-002 — Type change/clear confirmation */}
      <Dialog open={!!typeChangeConfirm} onOpenChange={(open) => { if (!open) setTypeChangeConfirm(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="type-change-confirm-modal">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {typeChangeConfirm?.newType ? `Switch Type to ${typeChangeConfirm.newType}?` : 'Clear the selected Type?'}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-1 text-left">
              {typeChangeConfirm?.newType ? 'Changing' : 'Clearing'} the Type will clear all information
              entered for this Mortgage / Deed of Trust record — instrument details, parties,
              trustee and all related sub-records (Assignments, Modifications, Appointed Trustees,
              Subordinations, Notices of Pendency). Comments are kept. Continue?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setTypeChangeConfirm(null)} data-testid="type-change-cancel-btn">
              Cancel
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={applyTypeChange} data-testid="type-change-confirm-btn">
              Confirm &amp; Clear
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* NO-OPEN-MORTGAGE-003 — destructive-clear confirmation */}
      <Dialog open={noOpenMortgageConfirm} onOpenChange={(open) => { if (!open) setNoOpenMortgageConfirm(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="no-open-mortgage-confirm-modal">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Clear all Mortgage / DOT information?
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-1 text-left">
              Selecting <span className="font-semibold">No Open Mortgage</span> will clear all existing
              Mortgage / Deed of Trust information for this order — including Instrument details, Parties,
              Assignments, Modifications, Appointed Trustees, Subordination Agreements and Notices of
              Pendency. Only Comments / Notes are kept. This cannot be undone. Continue?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setNoOpenMortgageConfirm(false)}
              data-testid="no-open-mortgage-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={applyNoOpenMortgage}
              data-testid="no-open-mortgage-confirm-btn"
            >
              Confirm &amp; Clear
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={statusModal.open} onOpenChange={(open) => {
        if (!open) {
          setStatusModal({ open: false, type: null });
          // If the user cancels without submitting, revert the instant visual
          // selection so the button doesn't stay painted as "pending/clarification".
          // We only clear when the real status hasn't already moved there.
          if (!submittingStatus) {
            if (activeAction === 'pending' && localStatus !== 'pending') setActiveAction(null);
            if (activeAction === 'clarification' && localStatus !== 'clarification') setActiveAction(null);
          }
        }
      }}>
        <DialogContent className="sm:max-w-lg" data-testid="status-change-modal">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              {statusModal.type === 'pending' ? (
                <><span className="h-7 w-7 rounded-lg bg-amber-50 flex items-center justify-center"><Clock className="h-3.5 w-3.5 text-amber-600" /></span> Mark as Pending</>
              ) : (
                <><span className="h-7 w-7 rounded-lg bg-orange-50 flex items-center justify-center"><AlertTriangle className="h-3.5 w-3.5 text-orange-600" /></span> Request Clarification</>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Quick Reason Chips */}
            {statusChipsLoaded && statusChips.length > 0 && (
              <div className={`rounded-lg border p-3 ${statusModal.type === 'pending' ? 'border-amber-200 bg-amber-50/30' : 'border-orange-200 bg-orange-50/30'}`} data-testid="quick-reason-chips">
                <div className="flex flex-wrap gap-1.5">
                  {statusChips.map((chip) => {
                    const isSelected = selectedChipIds.has(chip.id);
                    const selectedCls = statusModal.type === 'pending'
                      ? 'bg-amber-500 text-white border-transparent shadow-sm'
                      : 'bg-orange-500 text-white border-transparent shadow-sm';
                    return (
                      <button
                        key={chip.id}
                        type="button"
                        onClick={() => handleChipClick(chip)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium border flex items-center gap-1 transition-all duration-150 cursor-pointer ${
                          isSelected
                            ? selectedCls
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 hover:border-gray-300'
                        }`}
                        data-testid={`quick-reason-chip-${chip.id}`}
                      >
                        {isSelected && <Check className="w-3 h-3" />}
                        {chip.label}
                      </button>
                    );
                  })}
                  {selectedChipIds.size > 0 && (
                    <button
                      type="button"
                      onClick={() => { setSelectedChipIds(new Set()); setStatusReason(''); }}
                      className="px-2 py-1 rounded-md text-xs font-medium text-red-500 hover:bg-red-50 border border-transparent hover:border-red-200 flex items-center gap-1 transition-all"
                      data-testid="clear-chips-btn"
                    >
                      <X className="w-3 h-3" /> Clear ({selectedChipIds.size})
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Reason Textarea */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1.5">
                <span>Reason</span>
                <span className="text-red-500">*</span>
                <span className="text-slate-400 font-normal text-[11px]">(min 10 chars)</span>
              </label>
              <div className="relative">
                <textarea
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors duration-150 resize-none text-sm hover:border-slate-300"
                  style={{ minHeight: '100px' }}
                  placeholder={statusModal.type === 'pending' ? 'Describe why this order is being set to pending...' : 'Describe what clarification is needed...'}
                  maxLength={2000}
                  data-testid="status-reason-textarea"
                />
                <div className="absolute bottom-2 right-2 pointer-events-none">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusReason.length >= 10 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {statusReason.length}/2000
                  </span>
                </div>
              </div>
              {statusReason.length > 0 && statusReason.length < 10 && (
                <span className="text-[11px] text-slate-500 mt-1 block">{10 - statusReason.length} more characters needed</span>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => {
                setStatusModal({ open: false, type: null });
                // Revert the instant visual selection if the real status hasn't moved there
                if (activeAction === 'pending' && localStatus !== 'pending') setActiveAction(null);
                if (activeAction === 'clarification' && localStatus !== 'clarification') setActiveAction(null);
              }} data-testid="status-modal-cancel">
                Cancel
              </Button>
              <Button
                onClick={handleSubmitStatus}
                disabled={submittingStatus || statusReason.trim().length < 10}
                className={statusModal.type === 'pending' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-orange-600 hover:bg-orange-700 text-white'}
                data-testid="status-modal-submit"
              >
                {submittingStatus ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
                {statusModal.type === 'pending' ? 'Set Pending' : 'Send Clarification'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══════════ PDF Extraction Modal ═══════════ */}
      <Dialog open={extractModalOpen} onOpenChange={setExtractModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="extract-modal">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-slate-900">
              Extract Legal Description from PDF
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Step 1: Select PDF */}
            {!extractedText && !extracting && !extractError && (
              <>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Select a PDF</label>
                  {pdfFiles.length > 0 ? (
                    <div className="space-y-1.5 max-h-[200px] overflow-y-auto" data-testid="pdf-file-list">
                      {pdfFiles.map(f => (
                        <label
                          key={f.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedFileId === f.id
                              ? 'border-blue-400 bg-blue-50'
                              : 'border-slate-200 hover:bg-slate-50'
                          }`}
                          data-testid={`pdf-option-${f.id}`}
                        >
                          <input
                            type="radio"
                            name="pdf-select"
                            value={f.id}
                            checked={selectedFileId === f.id}
                            onChange={() => setSelectedFileId(f.id)}
                            className="accent-blue-600"
                          />
                          <FileText className="h-4 w-4 text-red-500 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-700 truncate">{f.filename}</p>
                            {f.file_size && (
                              <p className="text-xs text-slate-400">{(f.file_size / 1024).toFixed(0)} KB</p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="border border-dashed border-slate-200 rounded-lg p-6 text-center">
                      <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">No PDF files found.</p>
                      <p className="text-xs text-slate-400 mt-1">Upload PDF documents via the <strong>Files</strong> tab first.</p>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="ghost"
                    onClick={() => setExtractModalOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleExtract}
                    disabled={!selectedFileId}
                    data-testid="extract-submit-btn"
                  >
                    Extract Text
                  </Button>
                </div>
              </>
            )}

            {/* Extracting spinner */}
            {extracting && (
              <div className="flex flex-col items-center justify-center py-10 gap-3" data-testid="extract-loading">
                <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                <p className="text-sm font-medium text-slate-600">Extracting…</p>
                {extractProgress?.total ? (
                  <div className="w-64" data-testid="extract-progress">
                    <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-blue-500 transition-[width] duration-500"
                           style={{ width: `${Math.round(100 * (extractProgress.page || 0) / extractProgress.total)}%` }} />
                    </div>
                    <p className="text-xs text-slate-400 mt-1.5 text-center">
                      Reading scanned page {Math.min((extractProgress.page || 0) + 1, extractProgress.total)} of {extractProgress.total} (OCR)
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">Scanned documents are read page by page — this can take a few minutes.</p>
                )}
              </div>
            )}

            {/* Error state — stays in the modal with Retry / Back */}
            {extractError && !extracting && (
              <div className="space-y-4" data-testid="extract-error">
                <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
                  <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-red-800">Extraction failed</p>
                    <p className="text-sm text-red-700 mt-0.5 break-words" data-testid="extract-error-message">{extractError}</p>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button
                    variant="ghost"
                    onClick={() => { setExtractError(''); setSelectedFileId(''); }}
                    data-testid="extract-error-back-btn"
                  >
                    Back
                  </Button>
                  <Button onClick={handleExtract} data-testid="extract-retry-btn">
                    <RotateCcw className="h-4 w-4 mr-1.5" /> Retry
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Preview extracted text */}
            {extractedText && !extracting && (
              <>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Extracted Text</label>
                    <span className="text-[10px] font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full" data-testid="extract-method-badge">
                      via {extractMethod === 'ocr' ? 'OCR' : 'Direct'} — {extractedFilename}
                    </span>
                  </div>
                  {extractNotice && (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 mb-2" data-testid="extract-notice">
                      <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800">{extractNotice}</p>
                    </div>
                  )}
                  <div
                    className="border border-slate-200 rounded-lg bg-slate-50 p-3 max-h-[250px] overflow-y-auto font-mono text-xs text-slate-700 whitespace-pre-wrap leading-relaxed"
                    data-testid="extract-preview"
                  >
                    {extractedText}
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="ghost"
                    onClick={() => { setExtractedText(''); setExtractNotice(''); setSelectedFileId(''); }}
                    data-testid="extract-back-btn"
                  >
                    Back
                  </Button>
                  {!data.legal_description ? (
                    <Button
                      onClick={() => applyExtractedText('use')}
                      className="bg-teal-600 hover:bg-teal-700"
                      data-testid="extract-use-btn"
                    >
                      Use Extracted Text
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => applyExtractedText('append')}
                        data-testid="extract-append-btn"
                      >
                        Append
                      </Button>
                      <Button
                        onClick={() => applyExtractedText('replace')}
                        className="bg-teal-600 hover:bg-teal-700"
                        data-testid="extract-replace-btn"
                      >
                        Replace Existing
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══════════ Final Review Modal (FINAL-REVIEW-001) ═══════════ */}
      <FinalReviewModal
        open={finalReview.open}
        actionType={finalReview.action}
        initialChecklist={savedChecklist}
        data={data}
        propertyState={orderData?.property_state}
        submitting={finalReviewSubmitting}
        onMiscNotesChange={(v) => update('misc_notes', v)}
        onCancel={() => setFinalReview({ open: false, action: null })}
        onConfirm={handleFinalReviewConfirm}
      />
      <ScrollToTopButton threshold={400} className="bottom-[4.75rem]" />
    </div>
  );
}
